import type { AppState, Machine } from './types';
import type { RandMachineIssues } from './tipuri-baza';
import { state, api, $, $$, toast, escHtml, truncate, todayStr, _timeAgo, customConfirm, customPrompt, customSelect, formatDateRO, formatDateTimeRO } from './core';
import {
    workEnrichmentsPanelHtml, workWireEnrichmentForm, workRefreshEnrichmentsPanel,
    WORK_LIFECYCLE_LABELS,
} from './work-session';
import { mountWfPdfViewer, syncWfPdfZoomButtons, type WfPdfViewerHandle } from './pdf-viewer';
import { colleaguePanelHtml, colleagueLoadBrief, colleagueWireChat, setPendingWorkflowLesson } from './colleague';
import { colegIntreaba } from './coleg';
import { confirmaScrierea, confirmaUnText } from './corectura';
import { deschideAjutor } from './ghid';
import { ordoneazaCuEchivalente } from './echivalente';

declare function openModal(btn: { dataset: { machine?: string; action?: string; interval?: string } }): void;

type WorkflowState = AppState & {
    currentRevision?: { iv: any; machineId: string; machineName: string } | null;
    revisionDetailOpenedAt?: number | null;
    lastRevisionList?: any[];
};

const wst = state as WorkflowState;

/* ═══ WORKFLOW REVIZIE — 3 ETAPE: Pregătire Piese → Ghidaj → Execuție ═══ */
export let _wfTimer: ReturnType<typeof setInterval> | null = null;
export let _wfSeconds = 0;
export let _wfMatchedParts: any[] = [];
export let _wfGuidanceMode = 'tablet';
export let _wfWorkflowId: number | null = null;
export let _wfErpChecked = 0;
export let _wfStockIssues = 0;

export function _wfIsGenericMaintenanceStep(text: unknown): boolean {
    if (!text || String(text).trim().length < 12) return false;
    var t = String(text).toLowerCase();
    try {
        t = t.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    } catch (e: unknown) {}
    var p = [
        /distribu(i|ie|ti).*manual/,
        /manualul\s+(catre|ctre)/,
        /cites?te\s+integral/,
        /intelege\s+complet.*manual/,
        /folose(s|ș)te\s+.*masina\s+doar/,
        /porne(s|ș)te\s+.*masina\s+doar/,
        /personalul\s+responsabil/,
        /doar\s+pentru\s+sarcin/,
        /inainte\s+de\s+utilizare.*manual/,
        /cititi\s+integral/,
        /cititi\s+.*manual/
    ];
    for (var i = 0; i < p.length; i++) {
        if (p[i].test(t)) return true;
    }
    return false;
}

/** Tabletă: split simultan pași + PDF (portrait stacked, landscape side-by-side). */
export function _wfIsTabletSplitLayout(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches;
}

/** URL deschidere secundară (tab nou) — fallback, nu UX principal. */
function _wfManualPdfOpenUrl(docId: number, page: number): string {
    return '/api/documents/' + docId + '/view?page=' + page;
}

function _wfUpdatePdfToolbarPage(page: number, total?: number): void {
    var label = String(page);
    if (total && total > 0) label += ' / ' + total;
    var deskPage = document.getElementById('wf-pdf-page-num');
    if (deskPage) deskPage.textContent = label;
    var newTabLink = document.getElementById('wf-pdf-newtab') as HTMLAnchorElement | null;
    if (newTabLink) {
        var docId = newTabLink.dataset.docId || '';
        newTabLink.href = docId ? _wfManualPdfOpenUrl(parseInt(docId, 10), page) : newTabLink.href;
    }
}

let _wfPdfViewer: WfPdfViewerHandle | null = null;

function _wfDestroyPdfViewer(): void {
    if (_wfPdfViewer) {
        _wfPdfViewer.destroy();
        _wfPdfViewer = null;
    }
}

export async function _apiPost(url: string, data: unknown): Promise<any> {
    const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    let body: any = null;
    try { body = await r.json(); } catch (_e) { body = null; }
    if (!r.ok) {
        const msg = (body && (body.error || body.detail)) || ('HTTP ' + r.status);
        const err: any = new Error(String(msg));
        err.status = r.status;
        err.body = body;
        throw err;
    }
    return body;
}

export function ensureRevisionDetailOverlay(): HTMLElement {
    let el = document.getElementById('revision-detail-overlay') as HTMLElement | null;
    if (el) return el;
    const html = `<div id="revision-detail-overlay" class="revision-detail-overlay hidden">
        <div class="revision-detail-panel wf-panel">
            <header class="wf-header">
                <div class="wf-header-left">
                    <h2 id="revision-detail-title" class="wf-title"></h2>
                    <p id="revision-detail-subtitle" class="wf-subtitle"></p>
                </div>
                <button type="button" id="wf-btn-filmeaza" class="btn"
                    style="margin-right:10px;background:#b00020;color:#fff;border:0;border-radius:8px;padding:8px 14px;font-weight:700"
                    title="Deschide camera și transmite lucrarea (POV) — se leagă automat de lucrarea asta">🎥 Filmează</button>
                <button type="button" id="revision-detail-close" class="alarm-close" aria-label="Închide">✕</button>
            </header>
            <div class="wf-stepper">
                <div class="wf-step-ind active" data-wfs="1"><span class="wf-step-num">1</span><span class="wf-step-lbl">Pregătire piese</span></div>
                <div class="wf-step-ind" data-wfs="2"><span class="wf-step-num">2</span><span class="wf-step-lbl">Mod ghidaj</span></div>
                <div class="wf-step-ind" data-wfs="3"><span class="wf-step-num">3</span><span class="wf-step-lbl">Execuție</span></div>
            </div>
            <div class="wf-body" id="wf-body"></div>
        </div>
    </div>`;
    const wrap = document.createElement('div');
    wrap.innerHTML = html.trim();
    el = (wrap.firstElementChild || wrap.firstChild) as HTMLElement;
    // 🎥 Filmează (camere POV, 2026-07-29): deschide pagina de transmisie în
    // tab separat, cu mașina lucrării în context — filmarea se leagă automat.
    const btnFilm = el.querySelector('#wf-btn-filmeaza') as HTMLElement | null;
    if (btnFilm) btnFilm.addEventListener('click', () => {
        let mid = '';
        try { mid = JSON.parse(sessionStorage.getItem('_activeWorkflow') || '{}').machineId || ''; } catch (e) {}
        // 30 sep 2026: și fișa (wf_id) — filmarea se leagă de EA, nu „ghicită" după nume și zi;
        // prefixul instalării (prin hub: /maint) — altfel se deschidea /live-ul hub-ului
        const qs = new URLSearchParams();
        if (mid) qs.set('machine_id', mid);
        if (_wfWorkflowId) qs.set('wf_id', String(_wfWorkflowId));
        const pref = location.pathname.replace(/\/(index\.html)?$/, '');
        window.open(pref + '/live' + (qs.toString() ? ('?' + qs.toString()) : ''), '_blank');
    });
    document.body.appendChild(el);
    document.getElementById('revision-detail-close')!.addEventListener('click', closeRevisionDetailView as unknown as EventListener);
    return el;
}

export function _wfSetStep(n: number): void {
    document.querySelectorAll('.wf-step-ind').forEach(function(s) {
        const hel = s as HTMLElement;
        var sn = parseInt(hel.dataset.wfs || '0', 10);
        hel.classList.remove('active', 'done');
        if (sn < n) hel.classList.add('done');
        else if (sn === n) hel.classList.add('active');
    });
}

export async function _wfRenderStep1(iv: any, machineId: string): Promise<void> {
    var body = document.getElementById('wf-body') as HTMLElement;
    body.innerHTML = '<p style="padding:24px;color:var(--text-muted)">Se caută piese potrivite...</p>';
    _wfSetStep(1);
    try {
        var res = await fetch('/api/maintenance_intervals/' + iv.id + '/matched_parts').then(function(r){return r.json();});
        // echivalentul (alt furnizor) vine imediat după produsul pe care îl înlocuiește
        _wfMatchedParts = ordoneazaCuEchivalente(Array.isArray(res) ? res : []);
    } catch (e: unknown) { _wfMatchedParts = []; }

    var isMaster = state.currentUser && (state.currentUser.role === 'master' || state.currentUser.role === 'engineer');
    var hasAiMapped = _wfMatchedParts.some(function(p: any) { return p.match_source === 'ai' || p.match_source === 'document' || p.match_source === 'manual' || p.match_source === 'echivalent'; });
    var hasKeyword = _wfMatchedParts.some(function(p: any) { return p.match_source === 'keyword'; });
    var allVerified = _wfMatchedParts.length > 0 && _wfMatchedParts.every(function(p: any) { return p.verified; });

    var html = '<div class="wf-step-content">';
    html += '<h3 class="wf-section-title">Verificare piese de schimb</h3>';

    if (hasAiMapped && allVerified) {
        html += '<p class="wf-hint" style="color:var(--green)">✓ Piese verificate și confirmate de master.</p>';
    } else if (hasAiMapped) {
        html += '<p class="wf-hint">Piese identificate prin AI. Verificați disponibilitatea în stoc.</p>';
    } else if (hasKeyword) {
        html += '<p class="wf-hint" style="color:var(--orange)">⚠ Piese identificate prin potrivire cuvinte cheie (nevalidate). Verificare manuală recomandată.</p>';
    } else {
        html += '<p class="wf-hint">Piesele au fost identificate automat din baza de date.</p>';
    }

    html += '<div class="wf-actions-row">';
    html += '<button class="btn btn-sm wf-btn-erp-all" onclick="window._wfErpCheckAll()">🔗 Verifică stoc ERP (toate)</button>';
    html += '<button class="btn btn-sm btn-outline">📋 Export listă</button>';
    if (isMaster && _wfMatchedParts.length > 0 && !allVerified) {
        html += '<button class="btn btn-sm" style="background:var(--green);color:#fff" onclick="window._wfVerifyAllParts(' + iv.id + ')">✓ Confirmă piese</button>';
    }
    if (isMaster) {
        html += '<button class="btn btn-sm btn-outline" onclick="window._wfAddPartDialog(' + iv.id + ',\'' + escHtml(machineId) + '\')">+ Adaugă piesă</button>';
    }
    html += '</div>';

    if (_wfMatchedParts.length > 0) {
        html += '<div class="wf-parts-table-wrap"><table class="wf-parts-table"><thead><tr>';
        html += '<th>Cod piesă</th><th>Descriere</th><th>Categorie</th><th>Match</th><th>Stoc ERP</th><th>✓ Manual</th>';
        if (isMaster) html += '<th>Acțiuni</th>';
        html += '</tr></thead><tbody>';
        _wfMatchedParts.forEach(function(p: any, i: number) {
            var srcIcon = p.match_source === 'ai' ? '🤖' : p.match_source === 'document' ? '📄' : p.match_source === 'manual' ? '👤' : p.match_source === 'echivalent' ? '⇄' : '🔍';
            var verifiedBadge = p.verified ? ' <span style="color:var(--green);font-size:.75rem">✓</span>' : '';
            var rowClass = p.match_source === 'keyword' ? ' style="opacity:0.75"' : '';
            html += '<tr' + rowClass + '>';
            html += '<td>' + (p._sau ? '<span style="color:var(--text-muted)">sau </span>' : '') + '<a href="#" class="wf-part-link" data-pn="' + escHtml(p.part_number || '') + '" data-mid="' + escHtml(p.machine_id || '') + '" style="color:#fb923c;text-decoration:underline;cursor:pointer;font-weight:600"><code class="wf-part-code">' + escHtml(p.part_number || '—') + '</code></a></td>';
            // la echivalent, condiția (fără amestec, nu la punctele calde…) și nota lucrării
            var cond = p.echivalent_pentru ? [p.echivalent_conditie, p.match_source === 'echivalent' ? p.mapping_notes : ''].filter(Boolean) : [];
            html += '<td>' + escHtml(p.description_ro || p.description_en || '—')
                + cond.map(function(x: string) { return '<div class="wf-hint" style="margin:4px 0 0">' + escHtml(x) + '</div>'; }).join('') + '</td>';
            html += '<td><span class="badge badge-blue">' + escHtml(p.category || '—') + '</span></td>';
            html += '<td><span class="wf-match-score">' + srcIcon + ' ' + p.match_score + '%' + verifiedBadge + '</span></td>';
            html += '<td><div class="wf-stock-cell">';
            html += '<span class="wf-stock-dot wf-stock-unknown" id="wf-erp-dot-' + i + '"></span>';
            html += '<span class="wf-stock-text" id="wf-erp-txt-' + i + '">Neverificat</span>';
            html += '<button type="button" class="wf-erp-btn" onclick="window._wfErpCheck(' + p.id + ',' + i + ')">Check</button>';
            html += '</div></td>';
            html += '<td style="text-align:center"><input type="checkbox" class="wf-manual-chk" data-idx="' + i + '"></td>';
            if (isMaster) {
                html += '<td>';
                if (p.mapping_id) {
                    html += '<button class="btn-xs" style="color:var(--red)" onclick="window._wfRemovePart(' + iv.id + ',' + p.mapping_id + ')">✕</button>';
                }
                html += '</td>';
            }
            html += '</tr>';
        });
        html += '</tbody></table></div>';
    } else {
        var partFromIv = iv.part_number || iv.lubricant || '';
        if (partFromIv) {
            html += '<div class="wf-no-match"><p>Auto-match nu a găsit piese, dar intervalul specifică: <code>' + escHtml(partFromIv) + '</code></p></div>';
        } else {
            html += '<div class="wf-no-match"><p>Nu sunt necesare piese de schimb (doar inspecție/verificare).</p></div>';
        }
    }

    html += '<div class="wf-summary-row">';
    html += '<div class="wf-summary-item"><span class="wf-summary-num" style="color:var(--accent)">' + _wfMatchedParts.filter(function(p: any) { return !p._sau; }).length + '</span><span class="wf-summary-label">Piese identificate</span></div>';
    html += '<div class="wf-summary-item"><span class="wf-summary-num" id="wf-erp-count" style="color:var(--text-dim)">0</span><span class="wf-summary-label">Verificate ERP</span></div>';
    html += '<div class="wf-summary-item"><span class="wf-summary-num" id="wf-manual-count" style="color:var(--text-dim)">0</span><span class="wf-summary-label">Confirmate manual</span></div>';
    html += '<div class="wf-summary-item"><span class="wf-summary-num" id="wf-stock-issues" style="color:var(--text-dim)">—</span><span class="wf-summary-label">Probleme stoc</span></div>';
    html += '</div>';
    html += '<div class="wf-nav-row">';
    html += '<button class="btn btn-outline" onclick="_clearWorkflowSession(true); closeRevisionDetailView(true)">Anulează</button>';
    html += '<button class="btn btn-primary" onclick="window._wfGoStep2()">Continuă → Alegere ghidaj</button>';
    html += '</div></div>';
    body.innerHTML = html;

    body.querySelectorAll('.wf-part-link').forEach(link => {
        link.addEventListener('click', (e) => {
            e.preventDefault();
            const pn = (link as HTMLElement).dataset.pn || '';
            const mid = (link as HTMLElement).dataset.mid || '';
            const overlay = document.getElementById('revision-detail-overlay');
            if (overlay) overlay.classList.add('hidden');
            const partsNav = document.querySelector('.nav-links a[data-section="parts"]') as HTMLElement;
            if (partsNav) partsNav.click();
            setTimeout(() => {
                const tabBtn = document.querySelector('[data-tab="parts-tab-catalog"]');
                if (tabBtn) (tabBtn as HTMLElement).click();
                setTimeout(() => {
                    const searchEl = document.getElementById('parts-search') as HTMLInputElement;
                    const machineEl = document.getElementById('parts-machine') as HTMLSelectElement;
                    if (machineEl) machineEl.value = mid || '';
                    if (searchEl && pn) searchEl.value = pn;
                    state.partsPage = 1;
                    const loadBtn = document.getElementById('parts-btn') as HTMLButtonElement;
                    if (loadBtn) loadBtn.click();
                }, 150);
            }, 300);
        });
    });

    body.addEventListener('change', function(e) {
        const tgt = e.target as HTMLElement;
        if (tgt.classList.contains('wf-manual-chk')) {
            var cnt = body.querySelectorAll('.wf-manual-chk:checked').length;
            var el2 = document.getElementById('wf-manual-count');
            if (el2) { el2.textContent = String(cnt); el2.style.color = cnt > 0 ? 'var(--green)' : 'var(--text-dim)'; }
        }
    });
}

(window as any)._wfErpCheck = async function(partId: number, idx: number): Promise<void> {
    var dot = document.getElementById('wf-erp-dot-' + idx);
    var txt = document.getElementById('wf-erp-txt-' + idx);
    if (!dot || !txt) return;
    txt.textContent = '...';
    try {
        var res = await fetch('/api/parts/' + partId + '/stock_check').then(function(r){return r.json();});
        dot.className = 'wf-stock-dot';
        if (res.stock_status === 'ok') { dot.classList.add('wf-stock-ok'); txt.textContent = res.stock_qty + ' în stoc'; txt.style.color = 'var(--green)'; }
        else if (res.stock_status === 'low') { dot.classList.add('wf-stock-low'); txt.textContent = res.stock_qty + ' în stoc (LOW)'; txt.style.color = 'var(--orange)'; _wfStockIssues++; }
        else { dot.classList.add('wf-stock-none'); txt.textContent = 'Stoc 0 — COMANDĂ!'; txt.style.color = 'var(--red)'; _wfStockIssues++; }
    } catch (e: unknown) { dot.className = 'wf-stock-dot wf-stock-none'; txt.textContent = 'Eroare ERP'; txt.style.color = 'var(--red)'; }
    _wfErpChecked++;
    var el1 = document.getElementById('wf-erp-count'); if (el1) { el1.textContent = String(_wfErpChecked); el1.style.color = 'var(--green)'; }
    var el2 = document.getElementById('wf-stock-issues'); if (el2) { el2.textContent = String(_wfStockIssues); el2.style.color = _wfStockIssues > 0 ? 'var(--red)' : 'var(--green)'; }
};

(window as any)._wfErpCheckAll = function(): void {
    _wfMatchedParts.forEach(function(p: any, i: number) { (window as any)._wfErpCheck(p.id, i); });
};

(window as any)._wfVerifyAllParts = async function(intervalId: number): Promise<void> {
    try {
        await api('/api/maintenance_intervals/' + intervalId + '/mapped_parts/verify', {method: 'POST'});
        toast('Piese confirmate!', 'success');
        var rev = (wst as any).currentRevision;
        if (rev) _wfRenderStep1(rev.iv, rev.machineId);
    } catch (e: unknown) { toast('Eroare la confirmare', 'error'); }
};

(window as any)._wfRemovePart = async function(intervalId: number, mappingId: number): Promise<void> {
    if (!await customConfirm('Eliminați această piesă din lista de piese necesare?', 'Eliminare piesă')) return;
    try {
        await api('/api/maintenance_intervals/' + intervalId + '/mapped_parts/' + mappingId, {method: 'DELETE'});
        toast('Piesă eliminată', 'info');
        var rev = (wst as any).currentRevision;
        if (rev) _wfRenderStep1(rev.iv, rev.machineId);
    } catch (e: unknown) { toast('Eroare', 'error'); }
};

(window as any)._wfAddPartDialog = async function(intervalId: number, machineId: string): Promise<void> {
    var query = await customPrompt('Cod sau descriere piesă:', '', 'Caută piesă');
    if (!query || query.trim().length < 2) return;
    try {
        var resp = await api('/api/parts?machine_id=' + encodeURIComponent(machineId) + '&q=' + encodeURIComponent(query.trim()) + '&per_page=15');
        var parts = resp && resp.data ? resp.data : (Array.isArray(resp) ? resp : []);
        if (!parts || parts.length === 0) {
            toast('Nicio piesă găsită pentru "' + query + '"', 'warning');
            return;
        }
        var options = parts.slice(0, 15).map(function(p: any) {
            return p.part_number + ' — ' + (p.description_ro || p.description_en || '').substring(0, 50);
        });
        var selIdx = await customSelect('Selectați piesa:', options, 'Piese găsite');
        if (selIdx === null || selIdx < 0 || selIdx >= parts.length) return;
        var selected = parts[selIdx];
        await api('/api/maintenance_intervals/' + intervalId + '/mapped_parts', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({part_id: selected.id, quantity: 1})
        });
        toast('Piesă adăugată: ' + selected.part_number, 'success');
        var rev = (wst as any).currentRevision;
        if (rev) _wfRenderStep1(rev.iv, rev.machineId);
    } catch (e: unknown) { toast('Eroare la adăugare', 'error'); }
};

(window as any)._wfGoStep2 = function(): void {
    _wfSetStep(2);
    var body = document.getElementById('wf-body') as HTMLElement;
    var html = '<div class="wf-step-content">';
    html += '<h3 class="wf-section-title">Selectează modul de ghidaj</h3>';
    html += '<p class="wf-hint">Cum dorește operatorul să fie ghidat în timpul intervenției?</p>';
    html += '<div class="wf-guidance-grid">';
    html += '<div class="wf-guidance-card" onclick="window._wfSelectGuidance(this,\'ar\')">';
    html += '<div class="wf-guidance-icon">🥽</div><div class="wf-guidance-title">Ochelari AR</div>';
    html += '<div class="wf-guidance-desc">Ghidaj pas cu pas proiectat în câmpul vizual. Mâinile libere.</div>';
    html += '<span class="badge badge-purple" style="margin-top:10px">HANDS-FREE</span></div>';
    html += '<div class="wf-guidance-card selected" onclick="window._wfSelectGuidance(this,\'tablet\')">';
    html += '<div class="wf-guidance-icon">📱</div><div class="wf-guidance-title">Tabletă</div>';
    html += '<div class="wf-guidance-desc">Interfață touch cu pași, imagini și video. Cameră pentru documentare.</div>';
    html += '<span class="badge badge-blue" style="margin-top:10px">STANDARD</span></div>';
    html += '<div class="wf-guidance-card" onclick="window._wfSelectGuidance(this,\'both\')">';
    html += '<div class="wf-guidance-icon">🥽📱</div><div class="wf-guidance-title">Ambele</div>';
    html += '<div class="wf-guidance-desc">AR pentru ghidaj vizual + tabletă pentru documentare și confirmare.</div>';
    html += '<span class="badge badge-teal" style="margin-top:10px">COMPLET</span></div>';
    html += '</div>';
    html += '<div class="wf-operator-row">';
    html += '<div class="wf-operator-field"><label class="wf-field-label">Operator</label>';
    html += '<input type="text" id="wf-operator-name" class="wf-input" placeholder="Nume operator..." value="' + escHtml(state.currentUser?.username || '') + '"></div>';
    var userRole = state.currentUser?.role || 'operator';
    var levelMap: Record<string, string> = { operator: 'Operator', master: 'Master', engineer: 'Inginer mentenanță' };
    var selectedLevel = levelMap[userRole] || 'Operator';
    html += '<div class="wf-operator-field"><label class="wf-field-label">Nivel</label>';
    html += '<select id="wf-operator-level" class="wf-input">';
    html += '<option value="Operator"' + (selectedLevel === 'Operator' ? ' selected' : '') + '>Operator</option>';
    html += '<option value="Master"' + (selectedLevel === 'Master' ? ' selected' : '') + '>Master</option>';
    html += '<option value="Inginer mentenanță"' + (selectedLevel === 'Inginer mentenanță' ? ' selected' : '') + '>Inginer mentenanță</option>';
    html += '</select></div></div>';
    html += '<div class="wf-nav-row">';
    html += '<button class="btn btn-outline" onclick="window._wfBackToStep1()">← Înapoi la piese</button>';
    html += '<button class="btn btn-green" onclick="window._wfGoStep3()">Începe intervenția →</button>';
    html += '</div></div>';
    body.innerHTML = html;
    _wfGuidanceMode = 'tablet';
};

(window as any)._wfSelectGuidance = function(el: HTMLElement, mode: string): void {
    document.querySelectorAll('.wf-guidance-card').forEach(function(c) { (c as HTMLElement).classList.remove('selected'); });
    el.classList.add('selected');
    _wfGuidanceMode = mode;
};

(window as any)._wfBackToStep1 = function(): void {
    var r = wst.currentRevision;
    if (r) _wfRenderStep1(r.iv, r.machineId);
};

(window as any)._wfGoStep3 = async function(): Promise<void> {
    _wfSetStep(3);
    var r = wst.currentRevision;
    if (!r) return;
    var iv = r.iv;
    var machineId = r.machineId;
    var operatorName = (document.getElementById('wf-operator-name') as HTMLInputElement | null)?.value || '';
    var operatorLevel = (document.getElementById('wf-operator-level') as HTMLSelectElement | null)?.value || 'Tehnician';

    var body = document.getElementById('wf-body') as HTMLElement;
    body.innerHTML = '<div style="padding:40px;text-align:center"><div class="spinner" style="margin:0 auto 16px"></div><p style="color:var(--text-muted)" id="wf-loading-msg">Se pregătește intervenția...</p></div>';
    var loadMsg = document.getElementById('wf-loading-msg');

    function _fetchWithTimeout(url: string, opts: RequestInit | null | undefined, ms?: number): Promise<Response> {
        var ctrl = new AbortController();
        var timer = setTimeout(function() { ctrl.abort(); }, ms || 15000);
        var fetchOpts = Object.assign({}, opts || {}, { signal: ctrl.signal });
        return fetch(url, fetchOpts).finally(function() { clearTimeout(timer); });
    }

    if (_wfWorkflowId) {
        if (loadMsg) loadMsg.textContent = 'Se reia sesiunea de lucru...';
    } else {
        try {
            if (loadMsg) loadMsg.textContent = 'Se creează sesiunea de lucru...';
            var wfRes = await _apiPost('/api/maintenance_workflow', {
                machine_id: machineId, interval_id: iv.id,
                operator_name: operatorName, operator_level: operatorLevel,
                guidance_mode: _wfGuidanceMode
            });
            _wfWorkflowId = wfRes.id;
            if (wfRes.resumed) {
                _wfSeconds = Number(wfRes.duration_seconds) || 0;
                _resumeStepsData = wfRes.steps_data || null;
                if (loadMsg) loadMsg.textContent = 'Se reia sesiunea de lucru (' + Math.floor(_wfSeconds / 60) + ' min)...';
                if (_wfSeconds > 0) {
                    toast('Sesiune reluată: ' + Math.floor(_wfSeconds / 60) + ' min, ' + (wfRes.steps_completed || 0) + '/' + (wfRes.steps_total || 0) + ' pași');
                }
            }
            try {
                var saved = JSON.parse(sessionStorage.getItem('_activeWorkflow') || '{}');
                saved.wfId = _wfWorkflowId;
                sessionStorage.setItem('_activeWorkflow', JSON.stringify(saved));
            } catch (e2: unknown) {}
        } catch (e: unknown) {
            _wfWorkflowId = null;
            console.warn('Workflow creation failed:', (e as Error).message);
        }
    }

    // Smart page lookup: if interval has document_id, find the correct page via backend
    if (iv.document_id && iv.id) {
        try {
            var _pgResp = await _fetchWithTimeout('/api/maintenance_intervals/' + iv.id + '/find_page', null, 5000);
            var _pgData = await _pgResp.json();
            if (_pgData && _pgData.page && _pgData.page > 0) {
                iv.page_number = _pgData.page;
            }
        } catch (e: unknown) {}
    }

    var _earlyManualPdf = !!(iv.document_id && iv.page_number);

    // FISA DE LUCRU: check for a pre-built procedure first (skip if using manual PDF)
    var activeProcedure: any = null;
    var _wfProcedureId: number | null = null;
    if (!_earlyManualPdf) {
        try {
            if (loadMsg) loadMsg.textContent = 'Se caută fișa de lucru validată...';
            var procResp = await _fetchWithTimeout('/api/procedures/for-interval/' + iv.id, null, 8000);
            var procData = await procResp.json();
            if (procData && procData.id && procData.steps && procData.steps.length > 0) {
                activeProcedure = procData;
                _wfProcedureId = procData.id;
            }
        } catch (e: unknown) { console.warn('Procedure fetch:', (e as Error).message); }
    }

    var matchedExtracts: any[] = [];
    var procSteps: any[] = [];
    var _wfAiContent = '';
    var _isProcedureBased = false;
    var _procedureSafetyNotes = '';
    var _procedureToolsList = '';

    if (activeProcedure) {
        _isProcedureBased = true;
        _procedureSafetyNotes = activeProcedure.safety_notes || '';
        _procedureToolsList = activeProcedure.tools_required || '';
        activeProcedure.steps.forEach(function(s: any, i: number) {
            procSteps.push({
                step_number: i + 1,
                _extractId: 0,
                _procedureStepId: s.id,
                instruction: s.instruction || '',
                is_checkpoint: !!s.safety_warning,
                safety_warning: s.safety_warning || '',
                document_id: s.document_id,
                document_page: s.document_page,
                chapter_title: s.chapter_title || '',
                parts_needed: s.parts_needed || '',
                tools: s.tools || '',
                estimated_minutes: s.estimated_minutes,
                photo_reference: s.photo_reference || '',
                notes: s.notes || '',
            });
        });
    } else if (!_earlyManualPdf) {
        // Fallback: matched extracts (existing logic) — skip if using manual PDF
        try {
            if (loadMsg) loadMsg.textContent = 'Se caută proceduri din documentație...';
            var meResp = await _fetchWithTimeout('/api/maintenance_intervals/' + iv.id + '/matched_extracts', null, 10000);
            var me = await meResp.json();
            if (Array.isArray(me)) {
                matchedExtracts = me.filter(function(x) {
                    var t = (x.extract_type || '').toLowerCase();
                    return (t === 'procedure' || t === 'instructiune') && (x.title_ro || x.content_ro);
                });
            }
        } catch (e: unknown) { console.warn('Matched extracts fetch:', (e as Error).message); }

        for (var ex of matchedExtracts) {
            try {
                var stResp = await _fetchWithTimeout('/api/extracts/' + ex.id + '/steps', null, 8000);
                var stData = await stResp.json();
                if (stData.steps && stData.steps.length > 0) {
                    stData.steps.forEach(function(s: any) { s._extractId = ex.id; s._extractTitle = ex.title_ro || ''; });
                    procSteps = procSteps.concat(stData.steps);
                    if (procSteps.length >= 5) break;
                }
            } catch (e: unknown) { console.warn('Steps fetch:', (e as Error).message); }
        }

        if (procSteps.length > 0) {
            var _wfFiltered = procSteps.filter(function(s) { return !_wfIsGenericMaintenanceStep(s.instruction || ''); });
            if (_wfFiltered.length > 0) {
                procSteps = _wfFiltered;
            } else {
                procSteps = [];
            }
        }

        if (procSteps.length === 0) {
            try {
                if (loadMsg) loadMsg.textContent = 'Se generează pașii cu AI (poate dura ~30s)...';
                var _wfAiMsg = 'Ești ghid de mentenanță pe teren. Utilaj: ' + machineId + '. Sarcină exactă: ' + (iv.action_ro || 'mentenanță') +
                    '\n\nGenerează DOAR pași tehnici operaționali (ce verifici, unde, cum) pentru ÎNCHEIEREA acestei sarcini pe mașină.' +
                    '\nNU include: distribuire/citire manual, reguli generale „folosește mașina doar pentru…", „pornește doar după…", politici companie.' +
                    '\nMaxim 8 pași, numerotați 1. 2. …, câte o frază scurtă per pas.';
                var chatResp = await _fetchWithTimeout('/api/chat', {
                    method: 'POST', headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({ message: _wfAiMsg, machine_id: machineId, context_module: 'maintenance' })
                }, 45000);
                var chatData = await chatResp.json();
                if (chatData.reply) {
                    _wfAiContent = chatData.reply;
                    var lines = chatData.reply.split('\n').filter(function(l: string) { return l.trim().length > 5; });
                    var stepNum = 0;
                    lines.forEach(function(line: string) {
                        var clean = line.replace(/^[\s#*\-\d.]+/, '').trim();
                        if (clean.length < 10) return;
                        if (/^(---|===|#{1,4}\s)/.test(line.trim())) return;
                        if (_wfIsGenericMaintenanceStep(clean)) return;
                        stepNum++;
                        var isSafety = /⚠|siguranț|atenți|pericol|nu\s+forț/i.test(clean);
                        procSteps.push({ step_number: stepNum, _extractId: 0, instruction: clean, is_checkpoint: isSafety });
                    });
                }
            } catch (e: unknown) {
                console.warn('AI chat failed:', (e as Error).message);
                toast('AI nu a putut genera pașii (timeout). Se continuă fără pași auto.', 'error');
            }
        }
    }

    var modeLabels: Record<string, string> = { ar: 'Ochelari AR', tablet: 'Tabletă', both: 'AR + Tabletă' };
    var _isResume = !!_wfWorkflowId;
    if (!_isResume) _wfSeconds = 0;
    if (_wfTimer) clearInterval(_wfTimer);

    // PDF-based execution: embed PDF inline (split view) when interval has document_id + page_number
    var _useManualPdf = _earlyManualPdf && !_isProcedureBased;
    var _manualPdfUrl = _useManualPdf ? '/api/documents/' + iv.document_id + '/view?page=' + iv.page_number + '&embed=1' : '';

    // Determine if we have ANY PDF to show (manual or from procedure steps)
    var _hasPdfContext = _useManualPdf;
    var _firstPdfDocId = iv.document_id || 0;
    var _firstPdfPage = iv.page_number || 1;
    if (!_hasPdfContext && procSteps.length > 0) {
        for (var pi = 0; pi < procSteps.length; pi++) {
            if (procSteps[pi].document_id && procSteps[pi].document_page) {
                _hasPdfContext = true;
                _firstPdfDocId = procSteps[pi].document_id;
                _firstPdfPage = procSteps[pi].document_page;
                break;
            }
        }
    }

    // Problemele nerezolvate ale utilajului — și cele „în lucru” (12 oct 2026: „Începe” le trece în in_progress,
    // iar bannerul nu le mai arăta). /api/machine_issues (avarii.py): rândul + numele utilajului.
    let _machineOpenIssues: (RandMachineIssues & { machine_name: string | null })[] = [];
    try {
        _machineOpenIssues = await api('/api/machine_issues?machine_id=' + encodeURIComponent(machineId) + '&status=open,in_progress');
    } catch { /* silent */ }

    var modeIcon = _wfGuidanceMode === 'ar' ? '🥽' : (_wfGuidanceMode === 'both' ? '🥽📱' : '📱');
    var html = '<div class="wf-step-content wf-exec-proc">';

    if (_machineOpenIssues.length > 0) {
        html += '<div class="wf-issues-banner" style="background:rgba(239,68,68,.08);border:1px solid rgba(239,68,68,.3);border-radius:10px;padding:12px 16px;margin-bottom:12px">';
        html += '<div style="font-weight:700;color:#ef4444;margin-bottom:6px">⚠ Această mașină are ' + _machineOpenIssues.length + ' problemă(e) nerezolvată(e):</div>';
        _machineOpenIssues.forEach(function(iss) {
            var sevBadge = iss.severity === 'critical' ? '🔴' : iss.severity === 'high' ? '🟠' : '🔵';
            html += '<div style="font-size:.88rem;padding:3px 0;color:var(--text)">' + sevBadge + ' <strong>' + escHtml(iss.title || '') + '</strong>';
            if (iss.reported_by) html += ' <span style="color:var(--text-muted);font-size:.8rem">(' + escHtml(iss.reported_by) + ')</span>';
            if (iss.status === 'in_progress') html += ' <span style="color:#f59e0b;font-size:.8rem;font-weight:700">▶ în lucru</span>';
            html += '</div>';
        });
        html += '</div>';
    }

    html += '<div class="wf-exec-header"><div>';
    html += '<h3 class="wf-section-title" style="margin-bottom:2px">' + escHtml(iv.action_ro || 'Revizie') + '</h3>';

    if (_useManualPdf) {
        html += '<p class="wf-hint" style="color:var(--green)">Procedură din manualul producătorului — pagina ' + iv.page_number + '</p>';
    } else if (_isProcedureBased) {
        html += '<p class="wf-hint" style="color:var(--green)">FIȘA DE LUCRU VALIDATĂ v' + (activeProcedure.version || 1) + '</p>';
    } else if (procSteps.length > 0 && !_wfAiContent) {
        html += '<p class="wf-hint" style="color:var(--yellow)">Pași sugerați din documentație (nevalidați)</p>';
    } else if (_wfAiContent) {
        html += '<p class="wf-hint" style="color:var(--yellow)">Pași generați de AI (nevalidați)</p>';
    }

    html += '<p class="wf-hint">Operator: ' + escHtml(operatorName || '—') + ' · Început: ' + formatDateTimeRO(new Date());
    if (activeProcedure && activeProcedure.avg_duration) {
        html += ' · Timp mediu: ~' + activeProcedure.avg_duration + ' min';
    }
    if (activeProcedure && activeProcedure.estimated_minutes) {
        html += ' · Estimat: ' + activeProcedure.estimated_minutes + ' min';
    }
    html += '</p></div>';
    html += '<div class="wf-exec-badges">';
    html += '<div class="wf-exec-badge"><span class="wf-exec-badge-icon">' + modeIcon + '</span><span class="wf-exec-badge-val">' + (modeLabels[_wfGuidanceMode] || '') + '</span></div>';
    var _initHH = String(Math.floor(_wfSeconds / 3600)).padStart(2, '0');
    var _initMM = String(Math.floor((_wfSeconds % 3600) / 60)).padStart(2, '0');
    var _initSS = String(_wfSeconds % 60).padStart(2, '0');
    html += '<div class="wf-exec-badge"><span class="wf-exec-badge-icon">⏱</span><span class="wf-exec-badge-val" id="wf-timer">' + _initHH + ':' + _initMM + ':' + _initSS + '</span></div>';
    html += '</div></div>';

    var totalSteps = 0;

    if (_useManualPdf) {
        totalSteps = 1;
        html += '<div class="wf-manual-pdf-section" style="margin:16px 0;padding:16px;background:rgba(0,180,255,.06);border:1px solid rgba(0,180,255,.25);border-radius:10px">';
        html += '<p style="font-size:1em;margin:0 0 12px;color:var(--text);text-align:center">Manualul producătorului — <strong>pagina ' + iv.page_number + '</strong></p>';
        html += '<div style="display:flex;align-items:center;gap:8px;justify-content:center;margin-bottom:12px">';
        html += '<label style="display:flex;align-items:center;gap:10px;font-size:1em;cursor:pointer">';
        html += '<input type="checkbox" class="proc-step-cb" data-step="1" style="width:20px;height:20px">';
        html += '<span>Confirm execuție procedură conform manual</span></label></div>';
        html += '<div style="text-align:center"><button class="btn btn-sm wf-correct-page-btn" data-interval-id="' + iv.id + '" data-current-page="' + iv.page_number + '" style="background:rgba(251,146,60,.1);border:1px solid rgba(251,146,60,.4);color:#fb923c;font-size:.9em;padding:6px 16px;cursor:pointer;border-radius:8px">⚠ Pagina greșită? Corectează</button>';
        if (_hasPdfContext) {
            html += ' <span class="wf-hint wf-manual-inline-hint">Manualul este afișat în panoul din dreapta ↗</span>';
        }
        html += '</div>';
        html += '</div>';
    } else if (procSteps.length > 0) {
        html += '<div class="wf-progress-row"><div class="wf-progress-bar"><div class="wf-progress-fill" id="wf-progress-fill" style="width:0%"></div></div><span class="wf-progress-text" id="wf-progress-text">0 / ' + procSteps.length + '</span></div>';
        html += '<div class="proc-steps-list">';
        totalSteps = procSteps.length;
        procSteps.forEach(function(s, i) {
            var stepNum = i + 1;
            var ckClass = s.is_checkpoint ? ' proc-step-checkpoint' : '';
            var ckIcon = s.is_checkpoint ? '<span class="proc-ck-icon" title="Punct de siguranță">⚠</span>' : '';
            html += '<div class="proc-step' + ckClass + '" data-step="' + stepNum + '" data-extract-id="' + (s._extractId || 0) + '" data-proc-step-id="' + (s._procedureStepId || 0) + '">';
            html += '<label class="proc-step-label"><input type="checkbox" class="proc-step-cb" data-step="' + stepNum + '">';
            html += '<span class="proc-step-num">' + stepNum + '</span>';
            html += '<span class="proc-step-text">' + ckIcon + escHtml(s.instruction || '') + '</span></label>';

            // Enriched step info for Fisa de Lucru
            var hasExtra = s.parts_needed || s.tools || s.safety_warning || s.document_id || s.photo_reference || s.notes;
            if (hasExtra) {
                html += '<div class="proc-step-extra">';
                if (s.safety_warning) {
                    html += '<div class="proc-step-safety">⚠ ' + escHtml(s.safety_warning) + '</div>';
                }
                if (s.tools) {
                    html += '<div class="proc-step-tools">🔧 ' + escHtml(s.tools) + '</div>';
                }
                if (s.parts_needed) {
                    html += '<div class="proc-step-parts">📦 ' + escHtml(s.parts_needed) + '</div>';
                }
                if (s.document_id && s.document_page) {
                    html += '<a class="proc-step-pdf-link wf-pdf-inline-link" href="#" data-doc-id="' + s.document_id + '" data-page="' + s.document_page + '">';
                    html += '📄 Manual p.' + s.document_page;
                    if (s.chapter_title) html += ' — ' + escHtml(s.chapter_title);
                    html += '</a>';
                }
                if (s.photo_reference) {
                    html += '<img class="proc-step-ref-photo" src="/uploads/' + escHtml(s.photo_reference) + '" alt="Referință" loading="lazy">';
                }
                if (s.notes) {
                    html += '<div class="proc-step-eng-notes">💡 ' + escHtml(s.notes) + '</div>';
                }
                if (s.estimated_minutes) {
                    html += '<span class="proc-step-time">~' + s.estimated_minutes + ' min</span>';
                }
                html += '</div>';
            }

            html += '<div class="proc-step-actions">';
            if (!_isProcedureBased && s._extractId) {
                html += '<button class="proc-ctx-btn" data-step="' + stepNum + '" data-extract-id="' + s._extractId + '">🔍 Sugestii AI</button>';
                html += '<button class="proc-img-btn" data-step="' + stepNum + '" data-extract-id="' + s._extractId + '">🖼 Imagini</button>';
                html += '<button class="proc-openpdf-btn" data-step="' + stepNum + '" data-extract-id="' + s._extractId + '">📄 Deschide PDF</button>';
            } else if (_isProcedureBased && s.document_id) {
                html += '<button class="proc-openpdf-btn" data-doc-id="' + s.document_id + '" data-page="' + (s.document_page || 1) + '">📄 Deschide PDF</button>';
            }
            html += '<button class="proc-note-btn btn-xs" data-step="' + stepNum + '">📝 Notă</button>';
            html += '</div>';
            html += '<div class="proc-pdf-context hidden" id="wf-ctx-' + stepNum + '"></div>';
            html += '<div class="proc-step-images hidden" id="wf-imgs-' + stepNum + '"></div>';
            html += '</div>';
        });
        html += '</div>';
    } else {
        var actionParts = (iv.action_ro || '').split(/\s*,\s*|\s+și\s+/i).map(function(s: string){return s.trim();}).filter(Boolean);
        if (actionParts.length === 0) actionParts = [iv.action_ro || 'Revizie'];
        totalSteps = actionParts.length;
        html += '<div class="wf-progress-row"><div class="wf-progress-bar"><div class="wf-progress-fill" id="wf-progress-fill" style="width:0%"></div></div><span class="wf-progress-text" id="wf-progress-text">0 / ' + totalSteps + '</span></div>';
        html += '<div class="proc-steps-list">';
        actionParts.forEach(function(part: string, i: number) {
            var stepNum = i + 1;
            html += '<div class="proc-step" data-step="' + stepNum + '">';
            html += '<label class="proc-step-label"><input type="checkbox" class="proc-step-cb" data-step="' + stepNum + '">';
            html += '<span class="proc-step-num">' + stepNum + '</span>';
            html += '<span class="proc-step-text">' + escHtml(part) + '</span></label>';
            html += '<button class="proc-note-btn btn-xs" data-step="' + stepNum + '">📝 Notă</button>';
            html += '</div>';
        });
        html += '</div>';
    }

    if (_wfAiContent) {
        html += '<details class="wf-ai-ref"><summary>📋 Instrucțiuni complete AI (referință)</summary>';
        html += '<div class="wf-ai-ref-content">' + _wfAiContent.replace(/\n/g, '<br>') + '</div></details>';
    }

    // Lessons from previous validated interventions
    if (activeProcedure && activeProcedure.lessons && activeProcedure.lessons.length > 0) {
        html += '<details class="wf-lessons-ref"><summary>💡 Probleme frecvente din lecții anterioare (' + activeProcedure.lessons.length + ')</summary><ul class="wf-lessons-list">';
        activeProcedure.lessons.forEach(function(l: any) {
            html += '<li><strong>' + escHtml(l.symptom || '') + '</strong>';
            if (l.cause) html += ' — Cauză: ' + escHtml(l.cause);
            if (l.actions_taken) html += '<br>Acțiuni: ' + escHtml(l.actions_taken);
            html += '</li>';
        });
        html += '</ul></details>';
    }

    // Safety and tools from procedure or extracts
    var safetyItems: string[] = [];
    var toolsItems: string[] = [];
    if (_isProcedureBased) {
        if (_procedureSafetyNotes) safetyItems.push(_procedureSafetyNotes);
        if (_procedureToolsList) toolsItems.push(_procedureToolsList);
    } else {
        safetyItems = matchedExtracts.filter(function(x){return x.safety_warnings;}).map(function(x){return x.safety_warnings;}).slice(0, 5);
        toolsItems = matchedExtracts.filter(function(x){return x.tools;}).map(function(x){return x.tools;}).slice(0, 5);
    }
    if (toolsItems.length > 0 || safetyItems.length > 0) {
        html += '<div class="wf-exec-info-cols">';
        if (toolsItems.length > 0) {
            html += '<div class="wf-exec-info-block"><h4 style="color:var(--accent)">🔧 Scule necesare</h4><ul>';
            toolsItems.forEach(function(t) { html += '<li>' + escHtml(t) + '</li>'; });
            html += '</ul></div>';
        }
        if (safetyItems.length > 0) {
            html += '<div class="wf-exec-info-block wf-safety-block"><h4 style="color:var(--red)">⚠ Avertismente siguranță</h4><ul>';
            safetyItems.forEach(function(t) { html += '<li>' + escHtml(t) + '</li>'; });
            html += '</ul></div>';
        }
        html += '</div>';
    }

    // Not validated banner
    if (!_isProcedureBased && procSteps.length > 0) {
        html += '<div class="wf-unvalidated-banner" style="background:var(--yellow-bg,#fff3cd);color:var(--yellow-text,#856404);padding:8px 12px;border-radius:6px;margin:8px 0;font-size:0.85em">';
        html += 'Această procedură <strong>nu este validată</strong> de inginer. Rezultatele vor fi trimise pentru verificare.';
        html += '</div>';
    }

    html += '<div class="wf-nav-row"><div class="wf-nav-left">';
    html += '<button class="btn" style="background:#f59e0b;color:#000;font-weight:700;padding:10px 20px;font-size:.95rem;border:none;border-radius:10px" onclick="window._wfSaveProgress(\'Progres salvat — poți continua mâine.\')">⏸ ' + WORK_LIFECYCLE_LABELS.pause + '</button>';
    html += '<button class="btn" style="background:rgba(96,165,250,.15);color:#60a5fa;font-weight:600;padding:10px 20px;font-size:.95rem;border:1px solid rgba(96,165,250,.4);border-radius:10px;margin-left:6px" onclick="window._wfShiftHandover()">🔄 Predare schimb</button></div>';
    html += '<div class="wf-nav-right">';
    html += '<button class="btn" style="background:rgba(255,255,255,.08);color:var(--text);font-weight:600;padding:10px 16px;font-size:.95rem;border:1px solid var(--border);border-radius:10px;margin-right:6px" onclick="window._wfAjutor()" title="Cum se lucrează în revizia ghidată">❓ Ajutor</button>';
    html += '<button class="btn" style="background:rgba(255,255,255,.08);color:var(--text);font-weight:600;padding:10px 20px;font-size:.95rem;border:1px solid var(--border);border-radius:10px" onclick="window._wfAddNote()">📝 Notă</button>';
    html += '<button class="btn" style="background:rgba(239,68,68,.12);color:#ef4444;font-weight:700;padding:10px 20px;font-size:.95rem;border:1px solid rgba(239,68,68,.3);border-radius:10px;margin-left:6px" onclick="window._wfReportIssue()">⚠ Problemă</button>';
    html += '<button class="btn" style="background:#6366f1;color:#fff;font-weight:700;padding:10px 20px;font-size:.95rem;border:none;border-radius:10px;margin-left:6px" onclick="window._wfPostponeWithReason()">⏳ Amân</button>';
    html += '<button class="btn" style="background:#00e676;color:#000;font-weight:700;padding:10px 22px;font-size:1rem;border:none;border-radius:10px" onclick="window._wfFinalize()">✓ ' + WORK_LIFECYCLE_LABELS.completeWorkflow + '</button>';
    html += '</div></div></div>';

    // Journal panel
    html += '<div id="wf-journal-panel" class="wf-journal-panel">';
    html += '<div class="wf-journal-header"><span>📋 Jurnal note</span><span id="wf-journal-count" class="wf-journal-badge">0</span></div>';
    html += '<div id="wf-journal-list" class="wf-journal-list"></div>';
    html += '</div>';

    // Coleg AI panel — context from past executions of this operation + chat.
    // Collapsible so it assists without intruding on the execution flow.
    html += '<details class="colleague-similar-details" id="wf-colleague-panel" open style="margin-top:14px">';
    html += '<summary>🧑‍🔧 Coleg AI — context și întrebări</summary>';
    html += colleaguePanelHtml('wf');
    html += '</details>';

    html += '<div id="wf-enrichments-mount">' + workEnrichmentsPanelHtml('wf') + '</div>';

    // Wrap in split view when PDF is available
    var bodyEl = document.getElementById('wf-body') as HTMLElement;
    if (_hasPdfContext) {
        _wfDestroyPdfViewer();
        var pdfOpenUrl = _wfManualPdfOpenUrl(_firstPdfDocId, _firstPdfPage);
        var splitHtml = '<div class="wf-split-container' + (_wfIsTabletSplitLayout() ? ' wf-split-tablet' : '') + '" id="wf-split-root">';
        splitHtml += '<div class="wf-split-left">' + html + '</div>';
        splitHtml += '<div class="wf-split-right">';
        splitHtml += '<div class="wf-pdf-toolbar">';
        splitHtml += '<span class="wf-pdf-label">📄 Manual — p. <span id="wf-pdf-page-num">' + _firstPdfPage + '</span></span>';
        splitHtml += '<div class="wf-pdf-nav">';
        splitHtml += '<button class="btn btn-sm btn-outline" id="wf-pdf-prev" title="Pagina anterioară">◀</button>';
        splitHtml += '<button class="btn btn-sm btn-outline" id="wf-pdf-next" title="Pagina următoare">▶</button>';
        splitHtml += '<button class="btn btn-sm btn-outline" id="wf-pdf-zoom-out" title="Micșorează">−</button>';
        splitHtml += '<button class="btn btn-sm btn-outline" id="wf-pdf-zoom-in" title="Mărește">+</button>';
        splitHtml += '<button class="btn btn-sm btn-outline" id="wf-pdf-zoom-reset" title="Potrivire pagină (dublu-click: pagină ↔ lățime)">⊡</button>';
        splitHtml += '<a class="btn btn-sm btn-outline wf-pdf-newtab-link" id="wf-pdf-newtab" data-doc-id="' + _firstPdfDocId + '" href="' + pdfOpenUrl + '" target="_blank" rel="noopener" title="Deschide în tab nou">↗</a>';
        splitHtml += '</div></div>';
        splitHtml += '<div id="wf-pdf-panel" class="wf-pdf-viewer-host"></div>';
        splitHtml += '</div></div>';
        bodyEl.innerHTML = splitHtml;
    } else {
        bodyEl.innerHTML = html;
    }
    (window as any)._wfActiveProcedureId = _wfProcedureId;

    // Coleg AI: brief with past executions of this operation + chat (fire-and-forget).
    colleagueWireChat('wf', () => ({
        context_type: 'workflow', workflow_id: _wfWorkflowId || 0, machine_id: machineId,
    }));
    colleagueLoadBrief('wf', {
        context_type: 'workflow', workflow_id: _wfWorkflowId || 0,
        machine_id: machineId, problem: iv.action_ro || '',
    }).catch(function() {});
    var firstStep = bodyEl.querySelector('.proc-step[data-step="1"]') as HTMLElement | null;
    if (firstStep && !firstStep.dataset.startedAt) firstStep.dataset.startedAt = new Date().toISOString();

    _wfTimer = setInterval(function() {
        _wfSeconds++;
        var hh = String(Math.floor(_wfSeconds / 3600)).padStart(2, '0');
        var mm = String(Math.floor((_wfSeconds % 3600) / 60)).padStart(2, '0');
        var ss = String(_wfSeconds % 60).padStart(2, '0');
        var tel = document.getElementById('wf-timer');
        if (tel) tel.textContent = hh + ':' + mm + ':' + ss;
    }, 1000);

    if (_resumeStepsData) {
        try {
            var _resumeSteps = JSON.parse(_resumeStepsData);
            if (Array.isArray(_resumeSteps)) {
                setTimeout(function() {
                    var restored = 0;
                    _resumeSteps.forEach(function(s: any) {
                        var isDone = s.done === true || s.checked === true;
                        if (isDone) {
                            var cb = document.querySelector('.proc-step-cb[data-step="' + s.step + '"]') as HTMLInputElement;
                            if (cb) {
                                cb.checked = true;
                                restored++;
                                var stepEl = cb.closest('.proc-step') as HTMLElement | null;
                                if (stepEl) {
                                    if (s.started_at) stepEl.dataset.startedAt = s.started_at;
                                    if (s.completed_at) stepEl.dataset.completedAt = s.completed_at;
                                    if (s.completed_by) stepEl.dataset.completedBy = s.completed_by;
                                    stepEl.classList.add('proc-step-done');
                                }
                            }
                        }
                    });
                    toast('Mentenanță reluată — restaurați ' + restored + ' pași și ' + Math.floor(_wfSeconds / 60) + ' min timp', 'success');
                }, 500);
            }
        } catch (_e) {}
        _resumeStepsData = null;
    }

    _refreshJournalPanel();
    if (_wfWorkflowId) {
        workWireEnrichmentForm('workflow', () => _wfWorkflowId, 'wf');
        workRefreshEnrichmentsPanel('workflow', _wfWorkflowId, 'wf');
    }

    // PDF split view navigation (PDF.js — desktop + tablet)
    if (_hasPdfContext) {
        var _wfPdfCurrentPage = _firstPdfPage;
        var _wfPdfCurrentDocId = _firstPdfDocId;
        var _wfPdfTotalPages = 0;
        var panelEl = document.getElementById('wf-pdf-panel');
        var zoomIn = document.getElementById('wf-pdf-zoom-in');
        var zoomOut = document.getElementById('wf-pdf-zoom-out');
        var zoomReset = document.getElementById('wf-pdf-zoom-reset');
        var _syncPdfZoomBtns = function() {
            syncWfPdfZoomButtons(_wfPdfViewer, zoomIn, zoomOut);
        };
        if (panelEl) {
            _wfPdfViewer = mountWfPdfViewer({
                container: panelEl,
                docId: _wfPdfCurrentDocId,
                page: _wfPdfCurrentPage,
                onPageChange: function(page, total) {
                    _wfPdfCurrentPage = page;
                    _wfPdfTotalPages = total;
                    _wfUpdatePdfToolbarPage(page, total);
                    _syncPdfZoomBtns();
                },
                onZoomChange: function() {
                    _syncPdfZoomBtns();
                },
            });
        }

        var _wfPdfLoadedDocId = _wfPdfCurrentDocId;

        var _wfNavigatePdf = function(docId: number, page: number) {
            _wfPdfCurrentDocId = docId;
            _wfPdfCurrentPage = Math.max(1, page);
            var newTabLink = document.getElementById('wf-pdf-newtab') as HTMLAnchorElement | null;
            if (newTabLink) {
                newTabLink.dataset.docId = String(docId);
                newTabLink.href = _wfManualPdfOpenUrl(docId, _wfPdfCurrentPage);
            }
            if (_wfPdfViewer) {
                if (docId === _wfPdfLoadedDocId) {
                    void _wfPdfViewer.goToPage(_wfPdfCurrentPage);
                } else {
                    _wfPdfLoadedDocId = docId;
                    void _wfPdfViewer.goToDoc(docId, _wfPdfCurrentPage);
                }
            } else {
                _wfUpdatePdfToolbarPage(_wfPdfCurrentPage);
            }
        };
        (window as any)._wfNavigatePdf = _wfNavigatePdf;

        var prevBtn = document.getElementById('wf-pdf-prev');
        var nextBtn = document.getElementById('wf-pdf-next');
        if (prevBtn) prevBtn.addEventListener('click', function() { _wfNavigatePdf(_wfPdfCurrentDocId, _wfPdfCurrentPage - 1); });
        if (nextBtn) nextBtn.addEventListener('click', function() { _wfNavigatePdf(_wfPdfCurrentDocId, _wfPdfCurrentPage + 1); });

        if (zoomIn) zoomIn.addEventListener('click', function() {
            _wfPdfViewer?.zoom(0.15);
            _syncPdfZoomBtns();
        });
        if (zoomOut) zoomOut.addEventListener('click', function() {
            _wfPdfViewer?.zoom(-0.15);
            _syncPdfZoomBtns();
        });
        if (zoomReset) zoomReset.addEventListener('click', function() {
            _wfPdfViewer?.resetZoom();
            _syncPdfZoomBtns();
        });
        _syncPdfZoomBtns();
    }

    function updateWfProgress() {
        var done = bodyEl.querySelectorAll('.proc-step-cb:checked').length;
        var fill = document.getElementById('wf-progress-fill');
        var txt = document.getElementById('wf-progress-text');
        if (fill) fill.style.width = (totalSteps > 0 ? Math.round(done / totalSteps * 100) : 0) + '%';
        if (txt) txt.textContent = done + ' / ' + totalSteps;
    }

    bodyEl.querySelectorAll('.proc-step-cb').forEach(function(cb) {
        const icb = cb as HTMLInputElement;
        icb.addEventListener('change', function() {
            var stepEl = icb.closest('.proc-step') as HTMLElement;
            stepEl.classList.toggle('proc-step-done', icb.checked);
            stepEl.querySelectorAll('.proc-ctx-btn, .proc-img-btn, .proc-openpdf-btn').forEach(function(b) { (b as HTMLButtonElement).disabled = icb.checked; });
            if (icb.checked) {
                var now = new Date().toISOString();
                stepEl.dataset.completedAt = now;
                stepEl.dataset.completedBy = (document.getElementById('wf-operator-name') as HTMLInputElement | null)?.value || 'operator';
                if (!stepEl.dataset.startedAt) stepEl.dataset.startedAt = now;
                var ctxDiv = stepEl.querySelector('.proc-pdf-context');
                if (ctxDiv) (ctxDiv as HTMLElement).style.display = 'none';
                var imgDiv = stepEl.querySelector('.proc-step-images');
                if (imgDiv) (imgDiv as HTMLElement).style.display = 'none';
                var ctxBtn = stepEl.querySelector('.proc-ctx-btn');
                if (ctxBtn) ctxBtn.textContent = '🔍 Sugestii AI';
                var imgBtn = stepEl.querySelector('.proc-img-btn');
                if (imgBtn) imgBtn.textContent = '🖼 Imagini';
                var sn = parseInt(icb.dataset.step || '0', 10);
                var nextStep = bodyEl.querySelector('.proc-step[data-step="' + (sn + 1) + '"]') as HTMLElement | null;
                if (nextStep && !nextStep.dataset.startedAt) nextStep.dataset.startedAt = now;
                if (nextStep) nextStep.scrollIntoView({behavior: 'smooth', block: 'center'});
            } else {
                stepEl.dataset.completedAt = '';
                stepEl.dataset.completedBy = '';
            }
            updateWfProgress();
        });
    });

    bodyEl.querySelectorAll('.proc-ctx-btn').forEach(function(btn) {
        const b = btn as HTMLButtonElement;
        b.addEventListener('click', async function(e) {
            e.stopPropagation();
            var stepNum = b.dataset.step;
            var extId = b.dataset.extractId;
            var ctxDiv = document.getElementById('wf-ctx-' + stepNum);
            if (!ctxDiv) return;
            if ((ctxDiv as HTMLElement).style.display !== 'none' && ctxDiv.dataset.loaded) {
                (ctxDiv as HTMLElement).style.display = 'none';
                b.textContent = '🔍 Sugestii AI';
                return;
            }
            if (!ctxDiv.dataset.loaded) {
                b.textContent = '⏳...';
                try {
                    var resp = await fetch('/api/extracts/' + extId + '/steps/' + stepNum + '/pdf_context');
                    var data = await resp.json();
                    var h = '';
                    if (data.results && data.results.length > 0) {
                        data.results.slice(0, 3).forEach(function(r: any) {
                            h += '<div class="ai-ctx-row">';
                            h += '<div class="ai-ctx-detail">' + escHtml((r.snippet || '').substring(0, 500)) + '</div>';
                            h += '<button class="proc-openpdf-btn" style="margin-top:4px" onclick="window.open(\'/api/documents/' + r.document_id + '/view?page=' + r.page + '\',\'_blank\')">📄 Pagina ' + r.page + '</button>';
                            h += '</div>';
                        });
                    } else {
                        h = '<div class="ai-ctx-row"><em>Nu s-au găsit sugestii pentru acest pas.</em></div>';
                    }
                    ctxDiv.innerHTML = h;
                    ctxDiv.dataset.loaded = '1';
                } catch (err: unknown) {
                    ctxDiv.innerHTML = '<div class="ai-ctx-row"><em>Eroare la încărcare</em></div>';
                }
            }
            (ctxDiv as HTMLElement).style.display = 'block';
            ctxDiv.classList.remove('hidden');
            b.textContent = '🔍 Ascunde sugestii';
        });
    });

    bodyEl.querySelectorAll('.proc-img-btn').forEach(function(btn) {
        const b = btn as HTMLButtonElement;
        b.addEventListener('click', async function(e) {
            e.stopPropagation();
            var stepNum = b.dataset.step;
            var extId = b.dataset.extractId;
            var imgDiv = document.getElementById('wf-imgs-' + stepNum);
            if (!imgDiv) return;
            if ((imgDiv as HTMLElement).style.display !== 'none' && imgDiv.dataset.loaded) {
                (imgDiv as HTMLElement).style.display = 'none';
                b.textContent = '🖼 Imagini';
                return;
            }
            if (!imgDiv.dataset.loaded) {
                b.textContent = '⏳...';
                try {
                    var resp = await fetch('/api/extracts/' + extId + '/steps/' + stepNum + '/images');
                    var imgs = await resp.json();
                    if (imgs.length === 0) {
                        imgDiv.innerHTML = '<p class="proc-no-images">Nu s-au găsit imagini pentru acest pas.</p>';
                    } else {
                        imgDiv.innerHTML = imgs.map(function(img: any) {
                            return '<div class="proc-img-wrap"><img src="/uploads/' + escHtml(img.file_path) + '" class="proc-step-diagram" loading="lazy"><span class="proc-img-caption">Pag. ' + (img.page_number + 1) + '</span></div>';
                        }).join('');
                    }
                    imgDiv.dataset.loaded = '1';
                } catch (err: unknown) {
                    imgDiv.innerHTML = '<p class="proc-no-images">Eroare la încărcare</p>';
                }
            }
            (imgDiv as HTMLElement).style.display = 'block';
            imgDiv.classList.remove('hidden');
            b.textContent = '🖼 Ascunde imagini';
        });
    });

    bodyEl.querySelectorAll('.proc-openpdf-btn').forEach(function(btn) {
        const b = btn as HTMLButtonElement;
        if (b.closest('.ai-ctx-row')) return;
        b.addEventListener('click', async function(e) {
            e.stopPropagation();
            var navFn = (window as any)._wfNavigatePdf;
            // Procedure-based PDF: direct doc_id + page
            if (b.dataset.docId) {
                var docId = parseInt(b.dataset.docId);
                var page = parseInt(b.dataset.page || '1');
                if (navFn) {
                    navFn(docId, page);
                } else {
                    window.open('/api/documents/' + docId + '/view?page=' + page, '_blank');
                }
                return;
            }
            var stepNum = b.dataset.step;
            var extId = b.dataset.extractId;
            if (!extId) return;
            b.textContent = '⏳...';
            try {
                var resp = await fetch('/api/extracts/' + extId + '/steps/' + stepNum + '/pdf_context');
                var data = await resp.json();
                if (data.results && data.results.length > 0) {
                    if (navFn) {
                        navFn(data.results[0].document_id, data.results[0].page);
                    } else {
                        window.open('/api/documents/' + data.results[0].document_id + '/view?page=' + data.results[0].page, '_blank');
                    }
                } else if (data.document_id) {
                    window.open('/api/documents/' + data.document_id + '/file', '_blank');
                } else {
                    toast('Nu s-a găsit PDF-ul', 'warning');
                }
            } catch (err: unknown) {
                toast('Eroare la deschidere PDF', 'error');
            }
            b.textContent = '📄 Deschide PDF';
        });
    });

    // Per-step note buttons
    bodyEl.querySelectorAll('.proc-note-btn').forEach(function(btn) {
        const b = btn as HTMLButtonElement;
        b.addEventListener('click', function(e) {
            e.stopPropagation();
            var sn = parseInt(b.dataset.step || '0', 10);
            (window as any)._wfAddNote(sn);
        });
    });

    // Inline PDF links within steps → navigate embedded iframe
    bodyEl.querySelectorAll('.wf-pdf-inline-link').forEach(function(link) {
        const a = link as HTMLAnchorElement;
        a.addEventListener('click', function(e) {
            e.preventDefault();
            var navFn = (window as any)._wfNavigatePdf;
            var docId = parseInt(a.dataset.docId || '0');
            var page = parseInt(a.dataset.page || '1');
            if (navFn && docId) {
                navFn(docId, page);
            } else if (docId) {
                window.open('/api/documents/' + docId + '/view?page=' + page, '_blank');
            }
        });
    });

    // Page correction button
    bodyEl.querySelectorAll('.wf-correct-page-btn').forEach(function(btn) {
        const b = btn as HTMLButtonElement;
        b.addEventListener('click', async function(e) {
            e.stopPropagation();
            var ivId = b.dataset.intervalId;
            var currentPage = b.dataset.currentPage;
            var newPage = await customPrompt('Pagina curentă: ' + currentPage + '\nIntroduceți pagina corectă:', currentPage || '', 'Corectare pagină manual');
            if (!newPage || isNaN(parseInt(newPage)) || parseInt(newPage) < 1) return;
            try {
                var resp = await api('/api/maintenance_intervals/' + ivId + '/correct_page', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({page: parseInt(newPage)})
                });
                if (resp.status === 'ok') {
                    toast('Pagina corectată: ' + currentPage + ' → ' + newPage, 'success');
                    b.textContent = 'Corectat: p.' + newPage;
                    b.disabled = true;
                } else {
                    toast(resp.error || 'Eroare la corectare', 'error');
                }
            } catch (ex) {
                toast('Eroare conexiune', 'error');
            }
        });
    });
};

(window as any)._wfPause = function(): void {
    if (_wfTimer) {
        clearInterval(_wfTimer); _wfTimer = null; toast('Timer oprit.');
    } else {
        _wfTimer = setInterval(function() {
            _wfSeconds++;
            var hh = String(Math.floor(_wfSeconds / 3600)).padStart(2, '0');
            var mm = String(Math.floor((_wfSeconds % 3600) / 60)).padStart(2, '0');
            var ss = String(_wfSeconds % 60).padStart(2, '0');
            var tel = document.getElementById('wf-timer');
            if (tel) tel.textContent = hh + ':' + mm + ':' + ss;
        }, 1000);
        toast('Timer reluat.');
    }
};

(window as any)._wfAddNote = async function(stepNum?: number): Promise<void> {
    const categories = [
        { value: 'observatie', label: 'Observație', icon: '👁' },
        { value: 'problema', label: 'Problemă', icon: '⚠' },
        { value: 'masurare', label: 'Măsurare', icon: '📏' },
        { value: 'altele', label: 'Altele', icon: '📋' },
    ];

    const result = await _showNoteDialog({
        title: stepNum ? 'Notă pentru pasul ' + stepNum : 'Adaugă notă',
        categories,
        stepNum: stepNum || null,
    });
    if (!result) return;
    // Corectura scrierii (1 nov 2026, Dan): nota se vede corectată și se aprobă înainte de salvare
    result.text = await confirmaUnText('Nota ta', result.text, 'fisa-nota');

    if (_wfWorkflowId) {
        try {
            var operatorName = (document.getElementById('wf-operator-name') as HTMLInputElement | null)?.value || '';
            const payload: any = {
                note: result.text, category: result.category,
                step: stepNum || null, operator: operatorName
            };
            if (result.photoId) payload.photo_id = result.photoId;
            await _apiPost('/api/maintenance_workflow/' + _wfWorkflowId + '/notes', payload);
            toast('Notă salvată', 'success');
            await _refreshJournalPanel();
        } catch (e: unknown) {
            toast('Notă salvată local (eroare server)', 'warning');
        }
    } else {
        toast('Notă: ' + result.text.substring(0, 50), 'info');
    }
};

function _showNoteDialog(opts: {
    title: string;
    categories: { value: string; label: string; icon: string }[];
    stepNum: number | null;
    defaultCategory?: string;
}): Promise<{ text: string; category: string; photoId?: number } | null> {
    return new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.className = 'custom-modal-overlay';
        overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:10000;display:flex;align-items:center;justify-content:center';

        const catOptions = opts.categories.map(c =>
            `<option value="${c.value}" ${c.value === (opts.defaultCategory || 'observatie') ? 'selected' : ''}>${c.icon} ${c.label}</option>`
        ).join('');

        // 5 aug 2026 — audit mobil, punctul 4: avea min-width:380px, mai lat decât
        // un telefon de 360px (ieșea lateral), și nici max-height — pe ecran scurt
        // butonul de salvare cădea sub marginea de jos. Acum se strânge la lățimea
        // ecranului și derulează pe verticală.
        const modal = document.createElement('div');
        modal.className = 'wf-dlg';
        modal.style.cssText = 'background:var(--card-bg,#1e293b);border-radius:14px;padding:24px;'
            + 'min-width:0;width:min(94vw,500px);max-height:88dvh;overflow-y:auto;'
            + 'box-shadow:0 20px 60px rgba(0,0,0,.4);border:1px solid var(--border,#334155)';
        modal.innerHTML = `
            <h3 style="margin:0 0 16px;color:var(--text);font-size:1.1rem">${escHtml(opts.title)}</h3>
            <div style="margin-bottom:12px">
                <label style="font-size:.85rem;color:var(--text-muted);display:block;margin-bottom:4px">Categorie</label>
                <select id="_note-dlg-cat" style="width:100%;padding:8px 12px;border-radius:8px;background:var(--input-bg,#0f172a);color:var(--text);border:1px solid var(--border);font-size:.95rem">
                    ${catOptions}
                </select>
            </div>
            <div style="margin-bottom:12px">
                <label style="font-size:.85rem;color:var(--text-muted);display:block;margin-bottom:4px">Notă</label>
                <textarea id="_note-dlg-text" rows="4" placeholder="Descrie observația..."
                    style="width:100%;padding:10px 12px;border-radius:8px;background:var(--input-bg,#0f172a);color:var(--text);border:1px solid var(--border);font-size:.95rem;resize:vertical;min-height:80px;font-family:inherit;box-sizing:border-box"></textarea>
            </div>
            <div style="margin-bottom:16px;display:flex;gap:8px;align-items:center">
                <label class="btn" style="background:rgba(255,255,255,.08);color:var(--text);padding:8px 14px;border-radius:8px;cursor:pointer;font-size:.85rem;border:1px solid var(--border);display:inline-flex;align-items:center;gap:6px">
                    📷 Foto
                    <input type="file" id="_note-dlg-photo" accept="image/*" capture="environment" style="display:none">
                </label>
                <span id="_note-dlg-photo-name" style="font-size:.8rem;color:var(--text-muted)"></span>
            </div>
            <div style="display:flex;justify-content:flex-end;gap:8px">
                <button id="_note-dlg-cancel" class="btn" style="padding:10px 20px;border-radius:8px;background:rgba(255,255,255,.08);color:var(--text);border:1px solid var(--border);font-size:.95rem;cursor:pointer">Anulează</button>
                <button id="_note-dlg-ok" class="btn" style="padding:10px 20px;border-radius:8px;background:#00e676;color:#000;font-weight:700;border:none;font-size:.95rem;cursor:pointer">Salvează</button>
            </div>`;

        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        let uploadedPhotoId: number | undefined;
        const photoInput = modal.querySelector('#_note-dlg-photo') as HTMLInputElement;
        const photoName = modal.querySelector('#_note-dlg-photo-name') as HTMLElement;
        photoInput.addEventListener('change', async () => {
            const file = photoInput.files?.[0];
            if (!file || !_wfWorkflowId) return;
            photoName.textContent = 'Se încarcă...';
            try {
                const fd = new FormData();
                fd.append('file', file);
                fd.append('description', 'Foto notă');
                const res = await fetch('/api/maintenance_workflows/' + _wfWorkflowId + '/media', { method: 'POST', body: fd });
                const json = await res.json();
                if (json.id) { uploadedPhotoId = json.id; photoName.textContent = '✓ ' + file.name; }
                else { photoName.textContent = 'Eroare upload'; }
            } catch { photoName.textContent = 'Eroare upload'; }
        });

        const textEl = modal.querySelector('#_note-dlg-text') as HTMLTextAreaElement;
        textEl.focus();

        function cleanup(val: { text: string; category: string; photoId?: number } | null) {
            overlay.remove();
            resolve(val);
        }

        modal.querySelector('#_note-dlg-cancel')!.addEventListener('click', () => cleanup(null));
        overlay.addEventListener('click', (e) => { if (e.target === overlay) cleanup(null); });
        modal.querySelector('#_note-dlg-ok')!.addEventListener('click', () => {
            const text = textEl.value.trim();
            if (!text) { textEl.style.borderColor = '#ef4444'; return; }
            const cat = (modal.querySelector('#_note-dlg-cat') as HTMLSelectElement).value;
            cleanup({ text, category: cat, photoId: uploadedPhotoId });
        });
        textEl.addEventListener('keydown', (e) => {
            if (e.ctrlKey && e.key === 'Enter') {
                (modal.querySelector('#_note-dlg-ok') as HTMLButtonElement).click();
            }
        });
    });
}

const _CATEGORY_META: Record<string, { icon: string; color: string; label: string }> = {
    observatie: { icon: '👁', color: '#60a5fa', label: 'Observație' },
    problema:   { icon: '⚠', color: '#ef4444', label: 'Problemă' },
    masurare:   { icon: '📏', color: '#a78bfa', label: 'Măsurare' },
    predare_schimb: { icon: '🔄', color: '#f59e0b', label: 'Predare schimb' },
    altele:     { icon: '📋', color: '#94a3b8', label: 'Altele' },
};

async function _refreshJournalPanel(): Promise<void> {
    if (!_wfWorkflowId) return;
    const listEl = document.getElementById('wf-journal-list');
    const countEl = document.getElementById('wf-journal-count');
    if (!listEl) {
        setTimeout(() => _refreshJournalPanel(), 500);
        return;
    }
    try {
        const resp = await fetch('/api/maintenance_workflow/' + _wfWorkflowId + '/notes');
        if (!resp.ok) return;
        const notes: any[] = await resp.json();
        if (countEl) countEl.textContent = String(notes.length);
        if (notes.length === 0) {
            listEl.innerHTML = '<div style="padding:12px;color:var(--text-muted);font-size:.85rem;text-align:center">Nicio notă încă</div>';
            return;
        }
        let html = '';
        notes.forEach((n: any) => {
            const meta = _CATEGORY_META[n.category] || _CATEGORY_META.altele;
            const time = n.at ? new Date(n.at).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' }) : '';
            html += `<div class="wf-journal-entry" style="border-left:3px solid ${meta.color}">`;
            html += `<div class="wf-journal-entry-header">`;
            html += `<span class="wf-journal-cat-badge" style="background:${meta.color}22;color:${meta.color}">${meta.icon} ${meta.label}</span>`;
            if (n.step) html += `<span style="font-size:.75rem;color:var(--text-muted)">Pas ${n.step}</span>`;
            html += `<span style="font-size:.75rem;color:var(--text-muted);margin-left:auto">${escHtml(time)}</span>`;
            html += `</div>`;
            html += `<div class="wf-journal-entry-text">${escHtml(n.text || '')}</div>`;
            const footParts: string[] = [];
            if (n.by) footParts.push(escHtml(n.by));
            if (n.photo_id) footParts.push('📷');
            if (n.issue_id) footParts.push('<span style="color:#ef4444">→ Problemă #' + n.issue_id + '</span>');
            if (footParts.length) html += `<div style="font-size:.75rem;color:var(--text-muted);margin-top:2px">${footParts.join(' · ')}</div>`;
            html += `</div>`;
        });
        listEl.innerHTML = html;
        listEl.scrollTop = listEl.scrollHeight;
    } catch (e) {
        console.warn('Journal refresh error:', e);
    }
}

(window as any)._wfReportIssue = async function(): Promise<void> {
    const r = wst.currentRevision;
    if (!r) { toast('Nu există context mașină', 'error'); return; }

    const result = await _showIssueDialog();
    if (!result) return;
    // Corectura scrierii (1 nov 2026, Dan): titlul și descrierea problemei, văzute corectate și aprobate
    [result.title, result.description] = await confirmaScrierea(
        [{ eticheta: 'Problema (titlu)', text: result.title }, { eticheta: 'Descriere', text: result.description || '' }],
        'fisa-problema');

    try {
        const operatorName = (document.getElementById('wf-operator-name') as HTMLInputElement | null)?.value || '';
        const res = await _apiPost('/api/machine_issues', {
            machine_id: r.machineId,
            workflow_id: _wfWorkflowId,
            reported_by: operatorName,
            title: result.title,
            description: result.description,
            severity: result.severity,
            category: result.category,
        });
        if (res.id && _wfWorkflowId) {
            await _apiPost('/api/maintenance_workflow/' + _wfWorkflowId + '/notes', {
                note: '[PROBLEMĂ RAPORTATĂ] ' + result.title,
                category: 'problema',
                operator: operatorName,
                issue_id: res.id,
            });
            await _refreshJournalPanel();
        }
        toast('Problemă raportată — va rămâne vizibilă până la rezolvare', 'success');
    } catch (e: unknown) {
        toast('Eroare la raportare: ' + (e as Error).message, 'error');
    }
};

function _showIssueDialog(): Promise<{ title: string; description: string; severity: string; category: string } | null> {
    return new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.className = 'custom-modal-overlay';
        overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:10000;display:flex;align-items:center;justify-content:center';

        // 5 aug 2026 — audit mobil, punctul 4 (vezi nota de la dialogul de notă):
        // min-width:400px depășea ecranul telefonului, fără derulare pe verticală.
        const modal = document.createElement('div');
        modal.className = 'wf-dlg';
        modal.style.cssText = 'background:var(--card-bg,#1e293b);border-radius:14px;padding:24px;'
            + 'min-width:0;width:min(94vw,520px);max-height:88dvh;overflow-y:auto;'
            + 'box-shadow:0 20px 60px rgba(0,0,0,.4);border:1px solid var(--border,#334155)';
        modal.innerHTML = `
            <h3 style="margin:0 0 16px;color:#ef4444;font-size:1.1rem">⚠ Raportează problemă</h3>
            <div style="margin-bottom:12px">
                <label style="font-size:.85rem;color:var(--text-muted);display:block;margin-bottom:4px">Titlu problemă *</label>
                <input id="_issue-dlg-title" type="text" placeholder="Ex: Senzor temperatură defect"
                    style="width:100%;padding:8px 12px;border-radius:8px;background:var(--input-bg,#0f172a);color:var(--text);border:1px solid var(--border);font-size:.95rem;box-sizing:border-box">
            </div>
            <div style="margin-bottom:12px;display:flex;gap:12px;flex-wrap:wrap">
                <div style="flex:1 1 140px;min-width:0">
                    <label style="font-size:.85rem;color:var(--text-muted);display:block;margin-bottom:4px">Severitate</label>
                    <select id="_issue-dlg-severity" style="width:100%;padding:8px 12px;border-radius:8px;background:var(--input-bg,#0f172a);color:var(--text);border:1px solid var(--border);font-size:.95rem">
                        <option value="low">Scăzut</option>
                        <option value="medium" selected>Mediu</option>
                        <option value="high">Ridicat</option>
                        <option value="critical">Critic</option>
                    </select>
                </div>
                <div style="flex:1 1 140px;min-width:0">
                    <label style="font-size:.85rem;color:var(--text-muted);display:block;margin-bottom:4px">Categorie</label>
                    <select id="_issue-dlg-cat" style="width:100%;padding:8px 12px;border-radius:8px;background:var(--input-bg,#0f172a);color:var(--text);border:1px solid var(--border);font-size:.95rem">
                        <option value="sensor">Senzor</option>
                        <option value="mechanical">Mecanic</option>
                        <option value="electrical">Electric</option>
                        <option value="pneumatic">Pneumatic</option>
                        <option value="hydraulic">Hidraulic</option>
                        <option value="other" selected>Altul</option>
                    </select>
                </div>
            </div>
            <div style="margin-bottom:16px">
                <label style="font-size:.85rem;color:var(--text-muted);display:block;margin-bottom:4px">Descriere detaliată</label>
                <textarea id="_issue-dlg-desc" rows="3" placeholder="Descrie problema în detaliu..."
                    style="width:100%;padding:10px 12px;border-radius:8px;background:var(--input-bg,#0f172a);color:var(--text);border:1px solid var(--border);font-size:.95rem;resize:vertical;min-height:60px;font-family:inherit;box-sizing:border-box"></textarea>
            </div>
            <div style="display:flex;justify-content:flex-end;gap:8px">
                <button id="_issue-dlg-cancel" class="btn" style="padding:10px 20px;border-radius:8px;background:rgba(255,255,255,.08);color:var(--text);border:1px solid var(--border);font-size:.95rem;cursor:pointer">Anulează</button>
                <button id="_issue-dlg-ok" class="btn" style="padding:10px 20px;border-radius:8px;background:#ef4444;color:#fff;font-weight:700;border:none;font-size:.95rem;cursor:pointer">Raportează</button>
            </div>`;

        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        const titleEl = modal.querySelector('#_issue-dlg-title') as HTMLInputElement;
        titleEl.focus();

        function cleanup(val: any) { overlay.remove(); resolve(val); }

        modal.querySelector('#_issue-dlg-cancel')!.addEventListener('click', () => cleanup(null));
        overlay.addEventListener('click', (e) => { if (e.target === overlay) cleanup(null); });
        modal.querySelector('#_issue-dlg-ok')!.addEventListener('click', () => {
            const title = titleEl.value.trim();
            if (!title) { titleEl.style.borderColor = '#ef4444'; return; }
            cleanup({
                title,
                description: (modal.querySelector('#_issue-dlg-desc') as HTMLTextAreaElement).value.trim(),
                severity: (modal.querySelector('#_issue-dlg-severity') as HTMLSelectElement).value,
                category: (modal.querySelector('#_issue-dlg-cat') as HTMLSelectElement).value,
            });
        });
    });
}

(window as any)._wfShiftHandover = async function(): Promise<void> {
    var newOp = await customPrompt('Nume operator nou (care preia lucrarea):', '', 'Predare schimb');
    if (!newOp || !newOp.trim()) return;
    var notes = await customPrompt('Observații predare (ce s-a făcut, ce a rămas):', '', 'Predare schimb') || '';
    var ss = (window as any)._wfGetStepsState();
    if (_wfWorkflowId) {
        try {
            var currentOp = (document.getElementById('wf-operator-name') as HTMLInputElement | null)?.value || '';
            await _apiPost('/api/maintenance_workflow/' + _wfWorkflowId + '/save_progress', {
                duration_minutes: Math.round(_wfSeconds / 60), duration_seconds: _wfSeconds, notes: '',
                steps_total: ss.total, steps_completed: ss.done, steps_data: ss.data
            });
            await _apiPost('/api/maintenance_workflow/' + _wfWorkflowId + '/shift_handover', {
                new_operator: newOp.trim(), new_level: 'Tehnician',
                notes: notes, steps_completed: ss.done
            });
            var handoverText = 'Predare schimb: ' + currentOp + ' → ' + newOp.trim() + '. Pași: ' + ss.done + '/' + ss.total + '.';
            if (notes) handoverText += ' Observații: ' + notes;
            await _apiPost('/api/maintenance_workflow/' + _wfWorkflowId + '/notes', {
                note: handoverText, category: 'predare_schimb', operator: currentOp
            });
            toast('Predare schimb efectuată către ' + newOp.trim(), 'success');
            var opEl = document.getElementById('wf-operator-name') as HTMLInputElement | null;
            if (opEl) opEl.value = newOp.trim();
            await _refreshJournalPanel();
        } catch (e: unknown) { toast('Eroare la predare: ' + (e as Error).message, 'error'); }
    } else { toast('Nu există workflow activ', 'error'); }
};

(window as any)._wfGetStepsState = function(): { total: number; done: number; data: string } {
    var bodyEl = document.getElementById('wf-body') as HTMLElement | null;
    if (!bodyEl) return { total: 0, done: 0, data: '[]' };
    var cbs = bodyEl.querySelectorAll('.proc-step-cb');
    var total = cbs.length;
    var done = 0;
    var stepsArr: Record<string, any>[] = [];
    cbs.forEach(function(cb) {
        const icb = cb as HTMLInputElement;
        var checked = icb.checked;
        if (checked) done++;
        var stepEl = icb.closest('.proc-step') as HTMLElement | null;
        var txt = stepEl ? ((stepEl.querySelector('.proc-step-text') as HTMLElement | null)?.textContent || '') : '';
        var entry: Record<string, any> = { step: icb.dataset.step, done: checked, text: txt.substring(0, 200) };
        if (stepEl) {
            if (stepEl.dataset.startedAt) entry.started_at = stepEl.dataset.startedAt;
            if (stepEl.dataset.completedAt) entry.completed_at = stepEl.dataset.completedAt;
            if (stepEl.dataset.completedBy) entry.completed_by = stepEl.dataset.completedBy;
            if (entry.started_at && entry.completed_at) {
                entry.duration_seconds = Math.round((new Date(entry.completed_at as string).getTime() - new Date(entry.started_at as string).getTime()) / 1000);
            }
        }
        stepsArr.push(entry);
    });
    return { total: total, done: done, data: JSON.stringify(stepsArr) };
};

// „Ajutor” din bara reviziei ghidate: situațiile ghidului pentru ecranul „workflow” (31 oct 2026)
(window as any)._wfAjutor = function(): void { void deschideAjutor('workflow'); };

(window as any)._wfFinalize = async function(): Promise<void> {
    var ss = (window as any)._wfGetStepsState();
    if (ss.total > 0 && ss.done < ss.total) {
        var msg = 'Nu ai completat toți pașii (' + ss.done + ' din ' + ss.total + ').\n\nNu poți finaliza revizia fără să confirmi toți pașii.\nVrei să salvezi progresul și să continui mai târziu?';
        if (await customConfirm(msg, '⚠ Pași necompletați')) {
            await (window as any)._wfSaveProgress('Progres salvat. Poți relua mentenanța.');
        }
        return;
    }
    if (!await customConfirm('Confirmi finalizarea reviziei?\nToți pașii sunt completați (' + ss.done + '/' + ss.total + ').', '✓ Finalizare revizie')) return;
    // Colegul AI (31 oct 2026): dacă observațiile din timpul lucrului nu spun nimic despre lucrare, o singură
    // întrebare; răspunsul intră ca observație a reviziei, lângă celelalte
    var coleg = _wfWorkflowId ? await colegIntreaba({ workflow_id: _wfWorkflowId }) : null;
    if (coleg && coleg.raspuns) coleg.raspuns = await confirmaUnText('Ce ai răspuns', coleg.raspuns, 'fisa-finalizare');
    if (coleg && coleg.raspuns && _wfWorkflowId) {
        try {
            await _apiPost('/api/maintenance_workflow/' + _wfWorkflowId + '/notes', {
                note: coleg.raspuns, category: 'observatie',
                operator: (document.getElementById('wf-operator-name') as HTMLInputElement | null)?.value || '',
            });
        } catch (_) { /* răspunsul pleacă oricum cu finalizarea (câmpul coleg) */ }
    }
    if (_wfTimer) { clearInterval(_wfTimer); _wfTimer = null; }
    var r = wst.currentRevision;
    if (!r) return;
    var _wfLogId = '';
    if (_wfWorkflowId) {
        try {
            var _rez: any = await _apiPost('/api/maintenance_workflow/' + _wfWorkflowId + '/complete', {
                duration_minutes: Math.round(_wfSeconds / 60), duration_seconds: _wfSeconds, notes: '',
                steps_total: ss.total, steps_completed: ss.done, steps_data: ss.data,
                procedure_id: (window as any)._wfActiveProcedureId || null,
                execution_data: ss.data,
                coleg: coleg,
            });
            _wfLogId = String((_rez && _rez.log_id) || '');
        } catch (e: unknown) {
            const msg = (e as Error)?.message || String(e);
            toast('Finalizarea a eșuat: ' + msg + '. Reîncearcă sau apasă „Salvează progres".', 'error');
            if (_wfTimer) { clearInterval(_wfTimer); _wfTimer = null; }
            _wfTimer = setInterval(function() { _wfSeconds++; }, 1000);
            return;
        }
    }
    var iv = r.iv;
    var machineId = r.machineId;
    // Coleg AI: remember the completed workflow so the classic log modal that
    // opens next can offer the lesson with full workflow context (field notes).
    if (_wfWorkflowId) {
        setPendingWorkflowLesson({
            workflow_id: _wfWorkflowId, machine_id: machineId, problem: iv.action_ro || '',
        });
    }
    // 2026-05-24 — Four-state: after closing a major workflow, if the machine
    // is STILL offline >threshold the user must decide what state to leave it in.
    try {
        await _maybeShowPostWorkflowStateModal(machineId, _wfWorkflowId);
    } catch (_) { /* defensiv — never block close */ }
    _clearWorkflowSession();
    var fakeBtn = { dataset: { machine: machineId, action: iv.action_ro || '', interval: String(iv.interval_hours || 0), ivid: String(iv.id || ''), wflogid: _wfLogId } };
    closeRevisionDetailView(true);
    openModal(fakeBtn);
};

async function _maybeShowPostWorkflowStateModal(machineId: string, wfId: number | null): Promise<void> {
    if (!machineId) return;
    let op: any = null;
    try {
        const r = await fetch(`/api/machines/${encodeURIComponent(machineId)}/operational-state`, { credentials: 'include' });
        if (!r.ok) return;
        op = await r.json();
    } catch (_) { return; }
    if (!op) return;
    // Show the modal ONLY when the machine is still offline beyond threshold
    // (state in 'maintenance'/'technical_pause'). If it's already 'active' we
    // just let the toast announce success and move on.
    const ms = op.maintenance_state || 'active';
    if (ms !== 'maintenance' && ms !== 'technical_pause') {
        toast(`✅ Workflow #${wfId || ''} finalizat. Linia ${machineId} este activă.`);
        return;
    }
    const offlineHours = Math.round(op.offline_duration_hours || 0);
    const isMaint = ms === 'maintenance';
    const old = document.getElementById('modal-post-workflow-state');
    if (old) old.remove();
    const modal = document.createElement('div');
    modal.id = 'modal-post-workflow-state';
    modal.className = 'golive-modal';
    modal.innerHTML = `
        <div class="golive-modal-backdrop"></div>
        <div class="golive-modal-content small">
            <h2>${wfId ? '✅ Workflow #' + wfId + ' finalizat' : '✅ Workflow finalizat'}</h2>
            <p class="golive-modal-subtitle">${escHtml(machineId)} e încă oprită de <strong>${offlineHours}h</strong>. Ce facem?</p>
            <div style="display:flex;flex-direction:column;gap:10px;margin:14px 0">
                <button type="button" class="btn btn-green" id="pws-restart">🔌 Repornesc acum (linia e gata)</button>
                <button type="button" class="btn btn-outline" id="pws-tech-pause">⏸ Marchez mentenanța încheiată (trece în PAUZĂ TEHNICĂ)</button>
                <button type="button" class="btn btn-outline" id="pws-more-work">🔧 Mai am de lucru (rămâne MENTENANȚĂ ÎN CURS)</button>
            </div>
            <div class="golive-modal-result" id="pws-result"></div>
        </div>`;
    document.body.appendChild(modal);
    modal.querySelector('.golive-modal-backdrop')?.addEventListener('click', () => modal.remove());
    document.getElementById('pws-restart')?.addEventListener('click', async () => {
        // The maintainer says "the line is physically ready". We only clear
        // the manual flag here; the actual offline_since reset happens when
        // telemetry catches up (power > threshold). No-op on the DB side
        // beyond clearing the flag.
        if (isMaint) {
            try {
                await fetch(`/api/machines/${encodeURIComponent(machineId)}/clear_maintenance_stop`, {
                    method: 'POST', credentials: 'include',
                });
            } catch (_) { /* defensiv */ }
        }
        toast(`${machineId}: în așteptarea telemetriei de pornire`);
        modal.remove();
    });
    document.getElementById('pws-tech-pause')?.addEventListener('click', async () => {
        if (isMaint) {
            try {
                await fetch(`/api/machines/${encodeURIComponent(machineId)}/clear_maintenance_stop`, {
                    method: 'POST', credentials: 'include',
                });
            } catch (_) {}
        }
        toast(`${machineId}: marcată PAUZĂ TEHNICĂ`);
        modal.remove();
    });
    document.getElementById('pws-more-work')?.addEventListener('click', async () => {
        // Stay in 'maintenance' — set / refresh the manual flag.
        try {
            await fetch(`/api/machines/${encodeURIComponent(machineId)}/mark_maintenance_stop`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ reason: 'Continuare mentenanță după workflow #' + (wfId || '') })
            });
            toast(`${machineId}: rămâne MENTENANȚĂ ÎN CURS`);
        } catch (_) {
            toast(`${machineId}: nu am putut marca`, 'warning');
        }
        modal.remove();
    });
}

(window as any)._wfSaveProgress = async function(message?: string): Promise<void> {
    if (_wfTimer) { clearInterval(_wfTimer); _wfTimer = null; }
    var ss = (window as any)._wfGetStepsState();
    if (_wfWorkflowId) {
        try {
            await _apiPost('/api/maintenance_workflow/' + _wfWorkflowId + '/save_progress', {
                duration_minutes: Math.round(_wfSeconds / 60), duration_seconds: _wfSeconds, notes: '',
                steps_total: ss.total, steps_completed: ss.done, steps_data: ss.data
            });
        } catch (e: unknown) {
            const msg = (e as Error)?.message || String(e);
            toast('Salvare progres eșuată: ' + msg, 'error');
            _wfTimer = setInterval(function() { _wfSeconds++; }, 1000);
            return;
        }
    }
    toast(message || 'Progres salvat.', 'success');
    closeRevisionDetailView(true);
    try { const { loadMyTasks } = await import('./tasks-calendar'); loadMyTasks(); } catch (_e) {}
};

(window as any)._wfPostponeWithReason = function(): void {
    var r = wst.currentRevision;
    if (!r) return;
    var existing = document.getElementById('wf-postpone-overlay');
    if (existing) existing.remove();
    var ov = document.createElement('div');
    ov.id = 'wf-postpone-overlay';
    ov.style.cssText = 'position:fixed;inset:0;z-index:1300;background:rgba(0,0,0,.85);display:flex;align-items:center;justify-content:center;padding:1rem';
    ov.innerHTML = '<div style="background:var(--panel,#1a2332);border:1px solid var(--border,#2a3a4a);border-radius:12px;max-width:460px;width:100%;padding:24px">' +
        '<h3 style="color:#818cf8;margin:0 0 16px;font-size:1.1rem">⏳ Amânare mentenanță</h3>' +
        '<p style="font-size:.85rem;color:var(--text-muted);margin:0 0 12px"><strong>' + _escHtml(r.machineId) + '</strong> — ' + _escHtml((r.iv.action_ro || '').substring(0, 80)) + '</p>' +
        '<label style="font-weight:600;font-size:.9rem;display:block;margin-bottom:4px">Motiv amânare <span style="color:#ef4444">*</span></label>' +
        '<select id="wf-pp-reason" style="width:100%;padding:10px;border-radius:8px;border:1px solid var(--border,#2a3a4a);background:var(--bg-main,#0f1923);color:var(--text-main,#e2e8f0);margin-bottom:12px;font-size:.95rem">' +
        '<option value="">— Selectează motivul —</option>' +
        '<option value="Nu s-a aprobat oprirea producției">Nu s-a aprobat oprirea producției</option>' +
        '<option value="Lipsă piesă de schimb">Lipsă piesă de schimb</option>' +
        '<option value="Lipsă unealtă / echipament">Lipsă unealtă / echipament</option>' +
        '<option value="Schimb de tură">Schimb de tură</option>' +
        '<option value="Alt motiv">Alt motiv</option></select>' +
        '<label style="font-weight:600;font-size:.9rem;display:block;margin-bottom:4px">Detalii</label>' +
        '<textarea id="wf-pp-notes" rows="3" placeholder="Descrie mai exact motivul..." style="width:100%;padding:10px;border-radius:8px;border:1px solid var(--border,#2a3a4a);background:var(--bg-main,#0f1923);color:var(--text-main,#e2e8f0);margin-bottom:12px;font-size:.95rem;resize:vertical"></textarea>' +
        '<label style="font-weight:600;font-size:.9rem;display:block;margin-bottom:4px">Se reia pe <span style="color:#ef4444">*</span> <span style="font-weight:400;color:var(--text-muted);font-size:.78rem">(zi/lună/an)</span></label>' +
        '<input type="date" id="wf-pp-date" style="width:100%;padding:10px;border-radius:8px;border:1px solid var(--border,#2a3a4a);background:var(--bg-main,#0f1923);color:var(--text-main,#e2e8f0);margin-bottom:8px;font-size:.95rem">' +
        '<div id="wf-pp-date-display" style="font-size:.8rem;color:#818cf8;margin-bottom:16px;font-weight:600"></div>' +
        '<div style="display:flex;gap:10px">' +
        '<button class="btn btn-green" id="wf-pp-save" style="flex:1;padding:12px;font-size:1rem;font-weight:700;border-radius:10px">💾 Salvează amânarea</button>' +
        '<button class="btn btn-outline" id="wf-pp-cancel" style="padding:12px 20px;border-radius:10px">Anulează</button></div></div>';
    document.body.appendChild(ov);
    document.getElementById('wf-pp-cancel')!.addEventListener('click', function() { ov.remove(); });

    var wfPpDateInput = document.getElementById('wf-pp-date') as HTMLInputElement | null;
    var wfPpDateDisplay = document.getElementById('wf-pp-date-display');
    if (wfPpDateInput && wfPpDateDisplay) {
        const inp = wfPpDateInput, afis = wfPpDateDisplay;   // `var`-urile nu rămân îngustate în funcția de mai jos
        inp.addEventListener('change', function() {
            afis.textContent = inp.value ? '→ ' + formatDateRO(inp.value) : '';
        });
    }

    document.getElementById('wf-pp-save')!.addEventListener('click', async function() {
        var reason = (document.getElementById('wf-pp-reason') as HTMLSelectElement).value;
        if (!reason) { toast('Selectează motivul amânării!', 'warning'); return; }
        var notes = (document.getElementById('wf-pp-notes') as HTMLTextAreaElement).value.trim();
        var newDate = (document.getElementById('wf-pp-date') as HTMLInputElement).value;
        if (reason === 'Alt motiv' && notes.length < 20) { toast('Pentru „Alt motiv" descrie în minimum 20 de caractere.', 'warning'); return; }
        if (!newDate) { toast('Alege ziua în care se reia lucrarea.', 'warning'); return; }
        if (newDate <= todayStr()) { toast('Ziua de reluare trebuie să fie în viitor.', 'warning'); return; }
        var fullReason = notes ? reason + ' — ' + notes : reason;
        // 30 sep 2026: amânarea de aici pleca fără `reason_code`, serverul o refuza (400), iar
        // eroarea era înghițită — DUPĂ ce workflow-ul fusese deja anulat. Mecanicul vedea
        // „Mentenanța a fost amânată", dar nu rămânea nimic. Acum amânarea se scrie ÎNTÂI;
        // workflow-ul se închide doar dacă ea a trecut.
        var COD: Record<string, string> = {
            'Nu s-a aprobat oprirea producției': 'no_production_stop',
            'Lipsă piesă de schimb': 'missing_part',
            'Lipsă unealtă / echipament': 'missing_tool',
            'Schimb de tură': 'shift_change',
            'Alt motiv': 'other',
        };
        var iv = r!.iv;
        try {
            await _apiPost('/api/operator_tasks', {
                interval_id: iv.id || null,
                machine_id: r!.machineId,
                task_type: 'assigned',
                description: iv.action_ro || '',
                status: COD[reason] === 'missing_part' ? 'waiting_parts' : 'postponed',
                reason_code: COD[reason] || 'other',
                reason_text: notes || null,
                postpone_reason: fullReason,
                postponed_until: newDate,
                due_date: newDate,
                replaces_calendar_date: todayStr(),
                notes: notes || null
            });
        } catch (e: unknown) {
            toast('Amânarea NU s-a salvat: ' + ((e as Error)?.message || String(e)), 'error');
            return;
        }
        if (_wfTimer) { clearInterval(_wfTimer); _wfTimer = null; }
        var ss = (window as any)._wfGetStepsState ? (window as any)._wfGetStepsState() : {total:0,done:0,data:[]};
        if (_wfWorkflowId) {
            try {
                await _apiPost('/api/maintenance_workflow/' + _wfWorkflowId + '/save_progress', {
                    duration_minutes: Math.round(_wfSeconds / 60),
                    duration_seconds: _wfSeconds,
                    steps_total: ss.total, steps_completed: ss.done, steps_data: ss.data
                });
                var operatorName = (document.getElementById('wf-operator-name') as HTMLInputElement | null)?.value || '';
                await _apiPost('/api/maintenance_workflow/' + _wfWorkflowId + '/notes', {
                    note: 'AMÂNAT: ' + fullReason + ' (reprogramat: ' + formatDateRO(newDate) + ')',
                    category: 'altele', operator: operatorName
                });
                await _apiPost('/api/maintenance_workflow/' + _wfWorkflowId + '/cancel', {});
            } catch (e: unknown) {
                console.warn('workflow amânat — închiderea sesiunii:', (e as Error)?.message || e);
            }
        }
        _clearWorkflowSession();
        toast('Mentenanța a fost amânată: ' + reason, 'info');
        ov.remove();
        closeRevisionDetailView(true);
        try { const { loadMyTasks } = await import('./tasks-calendar'); loadMyTasks(); } catch (_e) {}
    });
};

function _escHtml(s: string): string {
    var d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
}

export function openRevisionDetailView(iv: any, machineId: Machine['id'], machineName: string, resumeWfId?: number): void {
    ensureRevisionDetailOverlay();
    wst.revisionDetailOpenedAt = Date.now();
    wst.currentRevision = { iv: iv, machineId: machineId, machineName: machineName };
    _wfErpChecked = 0; _wfStockIssues = 0; _wfMatchedParts = []; _wfWorkflowId = null;
    if (_wfTimer) { clearInterval(_wfTimer); _wfTimer = null; }
    if (!resumeWfId) _wfSeconds = 0;

    sessionStorage.setItem('_activeWorkflow', JSON.stringify({
        iv, machineId, machineName, startedAt: Date.now()
    }));

    var titleEl = document.getElementById('revision-detail-title');
    var subtitleEl = document.getElementById('revision-detail-subtitle');
    if (titleEl) titleEl.textContent = iv.action_ro || 'Revizie';
    if (subtitleEl) subtitleEl.textContent = (machineName || machineId) + ' · ' + (iv.interval_text || '—') + ' · ' + (iv.interval_hours || 0) + 'h';

    var overlayEl = document.getElementById('revision-detail-overlay');
    if (overlayEl) overlayEl.classList.remove('hidden');

    if (resumeWfId) {
        _resumeFromPausedWorkflow(iv, machineId, resumeWfId);
    } else {
        _wfRenderStep1(iv, machineId);
    }
}

let _resumeStepsData: string | null = null;

async function _resumeFromPausedWorkflow(iv: any, machineId: string, wfId: number): Promise<void> {
    try {
        var wfData = await _apiPost('/api/maintenance_workflow/' + wfId + '/resume', {});
        _wfWorkflowId = wfId;
        _wfSeconds = wfData.duration_seconds || (wfData.duration_minutes || 0) * 60;
        _resumeStepsData = wfData.steps_data || null;
        try {
            var saved = JSON.parse(sessionStorage.getItem('_activeWorkflow') || '{}');
            saved.wfId = wfId;
            sessionStorage.setItem('_activeWorkflow', JSON.stringify(saved));
        } catch (_e) {}

        (window as any)._wfGoStep3();
    } catch (e) {
        toast('Eroare la reluare: ' + (e as Error).message, 'error');
        _wfRenderStep1(iv, machineId);
    }
}

export async function _tryResumeWorkflow(): Promise<void> {
    try {
        var saved = sessionStorage.getItem('_activeWorkflow');
        if (!saved) return;
        var wf = JSON.parse(saved);
        if (!wf.iv || !wf.machineId) return;
        if (Date.now() - wf.startedAt > 8 * 3600 * 1000) {
            sessionStorage.removeItem('_activeWorkflow');
            return;
        }
        var selIdx = await customSelect(
            'Ai o mentenanță în desfășurare:\n' + (wf.iv.action_ro || 'Revizie').substring(0, 80),
            ['Continuă mentenanța', 'Anulează definitiv'],
            'Mentenanță activă'
        );
        if (selIdx === 0) {
            openRevisionDetailView(wf.iv, wf.machineId, wf.machineName, wf.wfId);
        } else if (selIdx === 1) {
            if (wf.wfId) {
                _apiPost('/api/maintenance_workflow/' + wf.wfId + '/cancel', {}).catch(function(){});
            }
            sessionStorage.removeItem('_activeWorkflow');
        }
    } catch (e: unknown) {}
}

(window as any).openRevisionDetailByIndex = function(idx: number): void {
    const list = wst.lastRevisionList;
    const w = window as any;
    const d = w._detailData;
    const machineId = w._detailLineId || (d && d.machine && d.machine.id);
    const machineName = (d && d.machine && d.machine.name) || machineId;
    if (!list || !Array.isArray(list) || !list[idx] || !machineId) return;
    openRevisionDetailView(list[idx], machineId, machineName);
};

/** @returns {boolean} true dacă overlay-ul e închis (sau era deja închis); false dacă utilizatorul a anulat promptul. */
export function closeRevisionDetailView(skipConfirm?: boolean): boolean {
    const overlayEl = document.getElementById('revision-detail-overlay');
    if (!overlayEl || overlayEl.classList.contains('hidden')) {
        return true;
    }
    if (!skipConfirm && _wfWorkflowId) {
        // 3 aug 2026 (25 workflow-uri fantomă, unele de 145 zile): doar te-ai
        // UITAT la revizie (zero pași bifați) → nu întrebăm nimic, anulăm
        // sesiunea pe server. Altfel rămânea «in_progress», punea mașina în
        // starea 'maintenance' și îi ASCUNDEA intervalele din calendar/liste.
        var ss0 = (window as any)._wfGetStepsState ? (window as any)._wfGetStepsState() : { total: 0, done: 0 };
        if (!ss0.done) {
            try { _apiPost('/api/maintenance_workflow/' + _wfWorkflowId + '/cancel', {}); } catch (e0: unknown) {}
            _wfWorkflowId = null;
            sessionStorage.removeItem('_activeWorkflow');
            if (_wfTimer) { clearInterval(_wfTimer); _wfTimer = null; }
            _wfDestroyPdfViewer();
            overlayEl.classList.add('hidden');
            wst.currentRevision = null;
            return true;
        }
        var existingBar = document.getElementById('wf-exit-bar');
        if (existingBar) { existingBar.remove(); return false; }
        var bar = document.createElement('div');
        bar.id = 'wf-exit-bar';
        bar.style.cssText = 'position:fixed;bottom:0;left:0;right:0;z-index:1200;background:linear-gradient(180deg,transparent,rgba(0,0,0,.95));padding:20px 24px;display:flex;gap:12px;justify-content:center;flex-wrap:wrap;animation:slideUp .2s ease';
        bar.innerHTML = '<button class="btn btn-sm" style="background:#f59e0b;color:#000;font-weight:700;padding:12px 24px;font-size:1rem;border-radius:10px" onclick="document.getElementById(\'wf-exit-bar\').remove();window._wfSaveProgress(\'Progres salvat în pauză.\')">⏸ Pauză</button>' +
            '<button class="btn btn-sm" style="background:#00e676;color:#000;font-weight:700;padding:12px 24px;font-size:1rem;border-radius:10px" onclick="document.getElementById(\'wf-exit-bar\').remove();window._wfFinalize()">✓ Finalizează</button>' +
            '<button class="btn btn-sm" style="background:#6366f1;color:#fff;font-weight:700;padding:12px 24px;font-size:1rem;border-radius:10px" onclick="document.getElementById(\'wf-exit-bar\').remove();window._wfPostponeWithReason()">⏳ Amân</button>' +
            '<button class="btn btn-sm" style="background:rgba(255,255,255,.1);color:var(--text-muted);padding:12px 24px;font-size:1rem;border-radius:10px" onclick="document.getElementById(\'wf-exit-bar\').remove()">Înapoi</button>';
        document.body.appendChild(bar);
        return false;
    }
    if (_wfTimer) { clearInterval(_wfTimer); _wfTimer = null; }
    _wfDestroyPdfViewer();
    overlayEl.classList.add('hidden');
    wst.currentRevision = null;
    return true;
}

export async function _clearWorkflowSession(cancelOnServer?: boolean): Promise<void> {
    if (cancelOnServer && _wfWorkflowId) {
        try { await _apiPost('/api/maintenance_workflow/' + _wfWorkflowId + '/cancel', {}); } catch (e: unknown) {}
    }
    sessionStorage.removeItem('_activeWorkflow');
}

/* Event listeners for revision overlay are now attached dynamically in ensureRevisionDetailOverlay() */
